import {
  Fragment,
  type ChangeEvent,
  type CSSProperties,
  type DragEvent,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useId,
} from "react";
import * as RadixContextMenu from "@radix-ui/react-context-menu";
import { useTranslation } from "react-i18next";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  canGroupComponents,
  useInterfaceBuilderSidebarActions,
} from "@/components/Layout/account/useInterfaceBuilderSidebarActions";
import { useInterfaceBuilderPropertyActions } from "@/components/Layout/account/useInterfaceBuilderPropertyActions";
import { Tooltip } from "@/components/UI/Tooltip";
import {
  getAccountComponentRoute,
  ROUTES,
} from "@/constants/navigation";
import {
  useDynamicSidebarItems,
  useUiComponents,
  useUiComponentSpecs,
} from "@/hooks/useDynamicSidebarItems";
import { useClipboardCopy } from "@/hooks/useClipboardCopy";
import { useProjects } from "@/hooks/useProjects";
import {
  type InterfaceBuilderSelection,
  useUser,
} from "@/contexts/UserContext";
import { getNextFloatingWindowStackOrder } from "@/components/Layout/account/floatingWindowStack";
import {
  removeComponentPropertyPreviewDraftsForComponent,
  upsertComponentPropertyPreviewDraft,
} from "@/interfaceBuilder/componentPropertyDrafts";
import {
  collectComponentSubtree,
} from "@/interfaceBuilder/componentSchemeTree";
import {
  buildSidebarItemManifestYaml,
  createSidebarItemManifestFileName,
  parseSidebarItemManifestYaml,
  YAML_MIME_TYPE,
} from "@/interfaceBuilder/sidebarItemManifest";
import {
  type BuilderProperty,
  type BuilderPropertyValue,
  createArrayItemBuilderProperties,
  createComponentBuilderProperties,
  createObjectBuilderProperties,
  createSidebarItemBuilderProperties,
  createRecordBuilderPropertiesFromRawObject,
  normalizeArrayPropertyValue,
} from "@/interfaceBuilder/propertyDefinitions";
import {
  readInterfaceBuilderInspectorWindowState,
  writeInterfaceBuilderInspectorWindowState,
  type InterfaceBuilderInspectorWindowState,
} from "@/storage/interfaceBuilderPreferences";
import { downloadTextFile } from "@/utils/fileDownload";
import {
  getUiSidebarItemComponentId,
  normalizeUiComponentId,
} from "@/utils/uiComponentLinks";
import type { StoredUiComponent } from "@/storage/uiComponentsRepository";
import type { StoredUiSidebarItem } from "@/storage/uiSidebarItemsRepository";
import "./InterfaceBuilderInspector.css";

interface BuilderTreeNode {
  componentUuid?: string;
  id: string;
  label: string;
  displayLabel?: string;
  icon?: "component" | "sidebarItem";
  tooltip?: {
    id: string;
    // A sidebar item's alone: the components are synced without their dates.
    dates?: {
      createdAt?: string;
      updatedAt?: string;
    };
  };
  properties: readonly BuilderProperty[];
  selection: InterfaceBuilderSelection;
  children: readonly BuilderTreeNode[];
}

interface BuilderTreeMenuItem {
  children?: readonly BuilderTreeMenuItem[];
  searchable?: boolean;
  disableWhenActionsDisabled?: boolean;
  disabled?: boolean;
  id: string;
  label: string;
  run?: () => Promise<boolean | void> | boolean | void;
  separatorBefore?: boolean;
  variant?: "danger";
}

type BuilderTreeMenuAction = NonNullable<BuilderTreeMenuItem["run"]>;

// What a modifier held during a tree click asks for. "additive" is Ctrl/Cmd --
// one node added to or dropped from the selection; "range" is Shift -- every
// node between the current selection and the clicked one.
type TreeSelectModifier = "additive" | "range" | null;

interface DragState {
  offsetX: number;
  offsetY: number;
}

interface SplitDragState {
  startTreeHeight: number;
  startY: number;
}

interface ObjectEditorFrame {
  draftValue: BuilderPropertyValue;
  left: number;
  positionReady: boolean;
  property: BuilderProperty;
  selectionId: string;
  stackOrder: number;
  top: number;
}

interface PropertyDraft {
  property: BuilderProperty;
  selection: InterfaceBuilderSelection;
  selectionId: string;
  value: BuilderPropertyValue;
}

interface ComponentDeleteConfirmation {
  // Every component the deletion removes, the nested ones included.
  affectedComponents: readonly StoredUiComponent[];
  // The components the deletion was asked for: one, or the whole selection.
  components: readonly StoredUiComponent[];
}

interface ObjectEditorDragState {
  index: number;
  offsetX: number;
  offsetY: number;
}

type ResizeEdge = "n" | "e" | "s" | "w" | "ne" | "se" | "sw" | "nw";

const WORKSPACE_NODE_ID = "workspace";
const SIDEBAR_NODE_ID = "sidebar";
const WINDOWS_NODE_ID = "windows";
const DEFAULT_WINDOW_WIDTH = 300;
const DEFAULT_WINDOW_HEIGHT = 420;
const MIN_WINDOW_WIDTH = 280;
const MIN_WINDOW_HEIGHT = 300;
const VIEWPORT_MARGIN = 12;
const OBJECT_EDITOR_MAX_WIDTH = 460;
const OBJECT_EDITOR_ESTIMATED_HEIGHT = 320;
const DEFAULT_TREE_HEIGHT = 210;
const MIN_TREE_HEIGHT = 110;
const MIN_PROPERTIES_HEIGHT = 110;
const INSPECTOR_CHROME_HEIGHT = 40;
const SPLITTER_HEIGHT = 6;
const TREE_NODE_TOOLTIP_DELAY_MS = 1000;
const NULL_SELECT_VALUE = "__interface-builder-null__";
const DEFAULT_EXPANDED_NODE_IDS = [
  WORKSPACE_NODE_ID,
  SIDEBAR_NODE_ID,
  WINDOWS_NODE_ID,
];
const PROPERTY_OBJECT_BUTTON_STYLE: CSSProperties = {
  alignSelf: "stretch",
  backgroundColor: "var(--ib-property-button-bg, var(--ib-bg))",
  border: "1px solid transparent",
  borderRadius: 0,
  color: "var(--ib-property-value-color, var(--color-link))",
  cursor: "pointer",
  display: "grid",
  fontFamily: "var(--font-mono)",
  fontSize: "inherit",
  gridTemplateColumns: "minmax(0, 1fr) var(--ib-property-action-size)",
  minWidth: 0,
  padding: 0,
  textAlign: "left",
  width: "100%",
};
const PROPERTY_OBJECT_BUTTON_DISABLED_STYLE: CSSProperties = {
  color: "var(--ib-muted)",
  cursor: "default",
};
const PROPERTY_OBJECT_SUMMARY_STYLE: CSSProperties = {
  minWidth: 0,
  overflow: "hidden",
  padding: "7px 8px",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const PROPERTY_OBJECT_AFFORDANCE_STYLE: CSSProperties = {
  backgroundColor: "var(--ib-property-button-affordance-bg, var(--ib-property-action-bg))",
  boxShadow: "inset 1px 0 0 var(--ib-border)",
  color: "var(--ib-property-button-affordance-color, var(--ib-property-value-color, var(--color-link)))",
  display: "grid",
  placeItems: "center",
};
const OBJECT_DIALOG_FOOTER_STYLE: CSSProperties = {
  backgroundColor: "var(--ib-bar)",
  borderTop: "1px solid var(--ib-border)",
  display: "flex",
  gap: "var(--space-2)",
  justifyContent: "flex-end",
  padding: "var(--space-3)",
};
const OBJECT_DIALOG_BUTTON_STYLE: CSSProperties = {
  marginTop: 0,
};
const EMPTY_PROPERTIES: readonly BuilderProperty[] = [];

const TREE_CONTEXT_MENU_STYLE: CSSProperties = {
  zIndex: "var(--z-context-menu)",
};
const RESIZE_EDGES: readonly ResizeEdge[] = [
  "n",
  "e",
  "s",
  "w",
  "ne",
  "se",
  "sw",
  "nw",
];

export function InterfaceBuilderInspector() {
  const { t } = useTranslation();
  const { copy: copyToClipboard } = useClipboardCopy();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    interfaceBuilderFocusComponentId,
    interfaceBuilderSelection,
    interfaceBuilderSelections,
    session,
    setInterfaceBuilderComponentPropertyDrafts,
    setInterfaceBuilderFocusComponentId,
    setInterfaceBuilderInspectorOpen,
    setInterfaceBuilderSelection,
    setInterfaceBuilderSelections,
    toggleInterfaceBuilderSelection,
  } = useUser();
  const rootRef = useRef<HTMLElement>(null);
  const [windowState, setWindowState] = useState(
    () => createInitialWindowState(),
  );
  const [inspectorStackOrder, setInspectorStackOrder] = useState(0);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [objectEditorStack, setObjectEditorStack] = useState<
    readonly ObjectEditorFrame[]
  >([]);
  const [objectEditorDragState, setObjectEditorDragState] =
    useState<ObjectEditorDragState | null>(null);
  const [splitDragState, setSplitDragState] =
    useState<SplitDragState | null>(null);
  const [propertyDrafts, setPropertyDrafts] = useState<readonly PropertyDraft[]>(
    [],
  );
  const [propertyDraftResetVersion, setPropertyDraftResetVersion] = useState(0);
  const [draggedTreeComponentId, setDraggedTreeComponentId] = useState<string | null>(
    null,
  );
  const [componentDeleteConfirmation, setComponentDeleteConfirmation] =
    useState<ComponentDeleteConfirmation | null>(null);
  const [manifestError, setManifestError] = useState<string | null>(null);
  const manifestInputRef = useRef<HTMLInputElement>(null);
  const dynamicSidebarItems = useDynamicSidebarItems({
    isAuthenticated: session.isAuthenticated,
    userId: session.user?.uuid,
    projectId: session.projectId,
  });
  const storedUiComponents = useUiComponents({
    isAuthenticated: session.isAuthenticated,
    userId: session.user?.uuid,
    projectId: session.projectId,
  });
  const componentSpecs = useUiComponentSpecs({
    isAuthenticated: session.isAuthenticated,
    userId: session.user?.uuid,
    projectId: session.projectId,
  });
  // A component list no longer carries the spec of every component, so each
  // one is handed the spec of its kind here, where its props are described.
  const uiComponents = useMemo(() => {
    const specsByKind = new Map(
      componentSpecs.map((componentSpec) => [componentSpec.kind, componentSpec.spec]),
    );

    return {
      components: storedUiComponents.components.map((component) => {
        const kind = component.props?.kind;
        const spec = typeof kind === "string" ? specsByKind.get(kind) : undefined;

        return spec ? { ...component, spec } : component;
      }),
    };
  }, [componentSpecs, storedUiComponents.components]);
  const { projects } = useProjects({ enabled: session.isAuthenticated });
  const {
    clearEditError,
    commitPropertyChanges,
    editError,
    editErrorDetails,
    submitting: propertySubmitting,
  } = useInterfaceBuilderPropertyActions({
    isAuthenticated: session.isAuthenticated,
    components: uiComponents.components,
    projectId: session.projectId,
    sidebarItems: dynamicSidebarItems.items,
    userId: session.user?.uuid,
  });
  const {
    actionError: sidebarActionError,
    actionErrorDetails: sidebarActionErrorDetails,
    clearActionError: clearSidebarActionError,
    cloneComponent,
    cloneSidebarItem,
    copyComponentToProject,
    createComponent,
    groupComponents,
    importSidebarItem,
    moveComponent,
    createSidebarItem,
    deleteComponents,
    hideSidebarItem,
    submitting: sidebarActionSubmitting,
  } = useInterfaceBuilderSidebarActions({
    isAuthenticated: session.isAuthenticated,
    userId: session.user?.uuid,
    projectId: session.projectId,
    components: uiComponents.components,
    sidebarItems: dynamicSidebarItems.items,
  });
  const treeActionsDisabled = (
    sidebarActionSubmitting
    || !session.isAuthenticated
    || !session.user?.uuid
    || !session.projectId
  );
  const expandedNodeIds = useMemo(
    () => new Set(windowState.expandedNodeIds),
    [windowState.expandedNodeIds],
  );
  const tree = useMemo(
    () => createBuilderTree({
      components: uiComponents.components,
      sidebarItems: dynamicSidebarItems.items,
      t,
    }),
    [
      dynamicSidebarItems.items,
      t,
      uiComponents.components,
    ],
  );
  const selectedNodePath = useMemo(
    () => (
      interfaceBuilderSelection
        ? findTreeNodePath(tree, interfaceBuilderSelection.id)
        : null
    ),
    [
      interfaceBuilderSelection,
      tree,
    ],
  );
  const selectedNode = selectedNodePath
    ? selectedNodePath[selectedNodePath.length - 1] ?? null
    : null;
  const selectedNodeId = selectedNode?.id ?? null;
  // The project a component or a menu item belongs to, read beside its own
  // properties. It is decided by the project the record was created in and the
  // server keeps it read-only, so it is shown rather than edited: handing a
  // component over to another project is what the tree menu is for.
  const selectedProperties = useMemo(() => {
    const properties = selectedNode?.properties ?? EMPTY_PROPERTIES;
    const projectId = readTreeNodeProjectId(
      selectedNode,
      uiComponents.components,
      dynamicSidebarItems.items,
    );
    if (!projectId) return properties;

    // A page of the administration project is read by every account without
    // that project being one of its own, so the id stands in for a name the
    // account cannot read.
    const project = projects.find(
      (currentProject) => currentProject.uuid === projectId,
    );

    return [
      {
        editable: false,
        id: "project_id",
        label: t("interfaceBuilderInspector.projectProperty"),
        nullable: false,
        path: ["project_id"],
        type: "string" as const,
        value: project?.name || projectId,
      },
      ...properties,
    ];
  }, [
    dynamicSidebarItems.items,
    projects,
    selectedNode,
    t,
    uiComponents.components,
  ]);
  // Ctrl/Cmd+click builds the selection in the tree and on the canvas alike, so
  // the node ids come from the shared selection rather than from tree state.
  const selectedNodeIds = useMemo(
    () => interfaceBuilderSelections.map((selection) => selection.id),
    [interfaceBuilderSelections],
  );
  const selectedNodeIdSet = useMemo(
    () => new Set(selectedNodeIds),
    [selectedNodeIds],
  );
  const selectedComponents = useMemo(() => {
    const componentsByUuid = new Map(
      uiComponents.components.map((component) => [component.uuid, component]),
    );

    return selectedNodeIds.flatMap((nodeId) => {
      const nodePath = findTreeNodePath(tree, nodeId);
      const node = nodePath ? nodePath[nodePath.length - 1] : null;
      const componentId = node ? getTreeNodeActionComponentId(node) : null;
      const component = componentId
        ? componentsByUuid.get(componentId)
        : undefined;

      return component ? [component] : [];
    });
  }, [
    selectedNodeIds,
    tree,
    uiComponents.components,
  ]);
  // Every selected node has to resolve to a component record, otherwise part of
  // the selection cannot be reparented into the group.
  const canGroupSelectedComponents = (
    selectedComponents.length === selectedNodeIds.length
    && canGroupComponents(selectedComponents)
  );
  useEffect(() => {
    if (!selectedNodePath || selectedNodePath.length <= 1) return;

    const ancestorNodeIds = selectedNodePath
      .slice(0, -1)
      .filter((node) => node.children.length > 0)
      .map((node) => node.id);
    if (ancestorNodeIds.length === 0) return;

    setWindowState((currentState) => {
      const expanded = new Set(currentState.expandedNodeIds);
      let changed = false;

      ancestorNodeIds.forEach((nodeId) => {
        if (expanded.has(nodeId)) return;

        expanded.add(nodeId);
        changed = true;
      });

      if (!changed) return currentState;

      return normalizeWindowState({
        ...currentState,
        expandedNodeIds: [...expanded],
      });
    });
  }, [selectedNodePath]);
  useLayoutEffect(() => {
    if (!selectedNodeId) return;

    const selectedButton = rootRef.current?.querySelector<HTMLElement>(
      '[data-interface-builder-tree-selected="true"]',
    );
    if (!selectedButton) return;

    if (document.activeElement !== selectedButton) {
      selectedButton.focus({ preventScroll: true });
    }
    if (typeof selectedButton.scrollIntoView !== "function") return;

    selectedButton.scrollIntoView({
      block: "nearest",
      inline: "nearest",
    });
  }, [
    selectedNodeId,
    windowState.expandedNodeIds,
  ]);
  const selectedPropertyDrafts = useMemo(() => (
    selectedNode
      ? propertyDrafts.filter((draft) => draft.selectionId === selectedNode.id)
      : []
  ), [
    propertyDrafts,
    selectedNode,
  ]);
  const hasPropertyDrafts = propertyDrafts.length > 0;
  const dirtyNodeIds = useMemo(
    () => new Set(propertyDrafts.map((draft) => draft.selectionId)),
    [propertyDrafts],
  );
  const handleOpenComponent = useCallback((componentId: string) => {
    navigate(getAccountComponentRoute(componentId));
  }, [navigate]);
  const handleComponentRemoved = useCallback((componentId: string) => {
    const selectedComponentUuid = selectedNode
      ? getTreeNodeActionComponentId(selectedNode)
      : null;

    if (
      selectionReferencesComponent(interfaceBuilderSelection, componentId)
      || selectedComponentUuid === componentId
    ) {
      setInterfaceBuilderSelection(null);
    }
    if (interfaceBuilderFocusComponentId === componentId) {
      setInterfaceBuilderFocusComponentId(null);
    }
    setInterfaceBuilderComponentPropertyDrafts((currentDrafts) => (
      removeComponentPropertyPreviewDraftsForComponent(
        currentDrafts,
        componentId,
      )
    ));
    if (readAccountComponentRouteId(location.pathname) === componentId) {
      navigate(ROUTES.account, { replace: true });
    }
  }, [
    interfaceBuilderFocusComponentId,
    interfaceBuilderSelection,
    location.pathname,
    navigate,
    selectedNode,
    setInterfaceBuilderComponentPropertyDrafts,
    setInterfaceBuilderFocusComponentId,
    setInterfaceBuilderSelection,
  ]);
  const handleSidebarItemRemoved = useCallback((item: StoredUiSidebarItem) => {
    const sidebarItemId = item.uuid;

    if (
      interfaceBuilderSelection?.kind === "sidebarItem"
      && interfaceBuilderSelection.sidebarItemId === sidebarItemId
    ) {
      setInterfaceBuilderSelection(null);
    }

    const linkedComponentId = normalizeUiComponentId(
      item.componentUuid || item.component?.uuid,
    );
    const routeItemId = getUiSidebarItemComponentId(item);
    if (
      !linkedComponentId
      && readAccountComponentRouteId(location.pathname) === routeItemId
    ) {
      navigate(ROUTES.account, { replace: true });
    }
  }, [
    interfaceBuilderSelection,
    location.pathname,
    navigate,
    setInterfaceBuilderSelection,
  ]);
  /**
   * Every component a sidebar item opens, and every container above it.
   *
   * Taking one of those out of the project would leave the item opening
   * nothing, and removing the item instead is not this action's to do: an item
   * of the administration project is read by every account and written by its
   * own, so the delete would be refused outright. A whole page travels as a
   * manifest, which carries its menu item with it.
   */
  const sidebarLinkedComponentIds = useMemo(() => {
    const componentsByUuid = new Map(
      uiComponents.components.map((component) => [component.uuid, component]),
    );
    const linkedIds = new Set<string>();

    dynamicSidebarItems.items.forEach((item) => {
      let currentId = normalizeUiComponentId(
        item.componentUuid || item.component?.uuid,
      );
      // Stops at a component already recorded, which is both the ancestors of
      // another item and a parent chain that loops.
      while (currentId && !linkedIds.has(currentId)) {
        linkedIds.add(currentId);
        currentId = componentsByUuid.get(currentId)?.parentUuid ?? null;
      }
    });

    return linkedIds;
  }, [dynamicSidebarItems.items, uiComponents.components]);
  // Moving is the copy plus the original: the component is only dropped once
  // the project it was handed to holds it.
  const handleMoveComponentToProject = useCallback(async (
    component: StoredUiComponent,
    projectId: string,
  ) => {
    if (!await copyComponentToProject(component, projectId)) return false;

    return deleteComponents([component], {
      onComponentRemoved: handleComponentRemoved,
    });
  }, [copyComponentToProject, deleteComponents, handleComponentRemoved]);
  const handleExportSidebarItem = useCallback((item: StoredUiSidebarItem) => {
    downloadTextFile(
      createSidebarItemManifestFileName(item),
      buildSidebarItemManifestYaml(item, uiComponents.components),
      YAML_MIME_TYPE,
    );
  }, [uiComponents.components]);
  const handleImportSidebarItem = useCallback(() => {
    setManifestError(null);
    manifestInputRef.current?.click();
  }, []);
  const handleManifestFileChange = useCallback(async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    // Cleared right away, so the same file can be picked again after a failed
    // import.
    event.target.value = "";
    if (!file) return;

    let manifest;
    try {
      manifest = parseSidebarItemManifestYaml(await file.text());
    } catch {
      setManifestError(t("interfaceBuilderInspector.manifestImportError"));
      return;
    }

    await importSidebarItem(manifest, {
      onSidebarItemOpened: handleOpenComponent,
    });
  }, [handleOpenComponent, importSidebarItem, t]);
  const handleTreeNodeSelect = useCallback((
    selection: InterfaceBuilderSelection,
    modifier: TreeSelectModifier,
  ) => {
    // Shift picks a range and Ctrl/Cmd picks one node at a time, so a click
    // holding both is read as the range: it is the wider of the two.
    if (modifier === "range") {
      // The range runs from the node the properties panel follows to the one
      // just clicked, over the tree as it is displayed -- so it covers exactly
      // the rows the click swept past, collapsed subtrees included as the
      // single row they are shown as.
      const range = collectTreeNodeRange(
        tree,
        expandedNodeIds,
        interfaceBuilderSelection?.id ?? null,
        selection.id,
      );
      if (range) {
        setInterfaceBuilderSelections(range);
        return;
      }

      // Nothing to range from (a first click, or an anchor that left the tree),
      // so the click stands on its own and becomes the anchor of the next one.
      setInterfaceBuilderSelection(selection);
      return;
    }

    if (modifier === "additive") {
      toggleInterfaceBuilderSelection(selection);
      return;
    }

    setInterfaceBuilderSelection(selection);
  }, [
    expandedNodeIds,
    interfaceBuilderSelection,
    setInterfaceBuilderSelection,
    setInterfaceBuilderSelections,
    toggleInterfaceBuilderSelection,
    tree,
  ]);
  const handleGroupSelectedComponents = useCallback(async () => {
    const grouped = await groupComponents(selectedComponents);
    if (!grouped) return false;

    // The grouped nodes moved in the tree, so their paths (and the ids built
    // from them) no longer address the same components.
    setInterfaceBuilderSelection(null);
    return true;
  }, [
    groupComponents,
    selectedComponents,
    setInterfaceBuilderSelection,
  ]);
  const openComponentDeleteConfirmation = useCallback((
    components: readonly StoredUiComponent[],
  ) => {
    // A selection may hold both a component and something nested inside it, so
    // the impact list names each component once.
    const affectedComponents = new Map<string, StoredUiComponent>();
    components.forEach((component) => {
      collectComponentSubtree(uiComponents.components, component).forEach(
        (affectedComponent) => {
          affectedComponents.set(affectedComponent.uuid, affectedComponent);
        },
      );
    });

    setComponentDeleteConfirmation({
      affectedComponents: [...affectedComponents.values()],
      components,
    });
  }, [uiComponents.components]);
  const runTreeMenuAction = useCallback((runAction: BuilderTreeMenuAction) => {
    void runAction();
  }, []);
  const handleTreeComponentMove = useCallback((
    componentId: string,
    parentUuid: string | null,
  ) => {
    const component = uiComponents.components.find(
      (currentComponent) => currentComponent.uuid === componentId,
    );
    if (!component) return;

    void moveComponent(component, parentUuid);
  }, [moveComponent, uiComponents.components]);
  const setTreeNodeSubtreeExpanded = useCallback((
    node: BuilderTreeNode,
    expanded: boolean,
  ) => {
    const nodeIds = collectExpandableTreeNodeIds(node);
    if (nodeIds.length === 0) return;

    setWindowState((currentState) => {
      const nextExpandedNodeIds = new Set(currentState.expandedNodeIds);
      let changed = false;

      nodeIds.forEach((nodeId) => {
        if (expanded) {
          if (nextExpandedNodeIds.has(nodeId)) return;
          nextExpandedNodeIds.add(nodeId);
          changed = true;
          return;
        }

        if (!nextExpandedNodeIds.has(nodeId)) return;
        nextExpandedNodeIds.delete(nodeId);
        changed = true;
      });

      if (!changed) return currentState;

      return normalizeWindowState({
        ...currentState,
        expandedNodeIds: [...nextExpandedNodeIds],
      });
    });
  }, []);
  const getTreeMenuItems = useCallback((node: BuilderTreeNode) => {
    const defaultSidebarItemName = t(
      "interfaceBuilderInspector.nodes.sidebarItemFallback",
    );
    // A menu opened on a node of a multi-selection acts on the whole selection,
    // which every selected node has to resolve to a component for.
    const deletesSelection = (
      selectedComponents.length > 1
      && selectedComponents.length === selectedNodeIds.length
      && selectedNodeIdSet.has(node.id)
    );
    const expansionMenuItems = createTreeExpansionMenuItems(
      node,
      t,
      setTreeNodeSubtreeExpanded,
    );
    // The kind submenu is shared between the Windows root (creates a top-level
    // component when parentUuid is omitted) and a component node (creates a
    // child). Keep every t() key a string literal so the i18n audit can see it.
    const createComponentKindMenuItems = (
      parentUuid?: string,
    ): readonly BuilderTreeMenuItem[] => {
      const create = (kind: string) => createComponent(
        {},
        { kind, ...(parentUuid !== undefined ? { parentUuid } : {}) },
      );
      return [
        {
          id: "create-component-view",
          label: t("interfaceBuilderInspector.treeMenu.createComponentKind.view"),
          run: () => create("view"),
        },
        {
          id: "create-component-panel",
          label: t("interfaceBuilderInspector.treeMenu.createComponentKind.panel"),
          run: () => create("panel"),
        },
        {
          id: "create-component-label",
          label: t("interfaceBuilderInspector.treeMenu.createComponentKind.label"),
          run: () => create("label"),
        },
        {
          id: "create-component-picture",
          label: t("interfaceBuilderInspector.treeMenu.createComponentKind.picture"),
          run: () => create("picture"),
        },
        {
          id: "create-component-line",
          label: t("interfaceBuilderInspector.treeMenu.createComponentKind.line"),
          run: () => create("line"),
        },
        {
          id: "create-component-qr-code",
          label: t("interfaceBuilderInspector.treeMenu.createComponentKind.qr_code"),
          run: () => create("qr_code"),
        },
        {
          id: "create-component-button",
          label: t("interfaceBuilderInspector.treeMenu.createComponentKind.button"),
          run: () => create("button"),
        },
        {
          id: "create-component-input",
          label: t("interfaceBuilderInspector.treeMenu.createComponentKind.input"),
          run: () => create("input"),
        },
        {
          id: "create-component-password",
          label: t("interfaceBuilderInspector.treeMenu.createComponentKind.password"),
          run: () => create("password"),
        },
        {
          id: "create-component-select",
          label: t("interfaceBuilderInspector.treeMenu.createComponentKind.select"),
          run: () => create("select"),
        },
        {
          id: "create-component-rating",
          label: t("interfaceBuilderInspector.treeMenu.createComponentKind.rating"),
          run: () => create("rating"),
        },
        {
          id: "create-component-window",
          label: t("interfaceBuilderInspector.treeMenu.createComponentKind.window"),
          run: () => create("window"),
        },
        {
          id: "create-component-data-source",
          label: t(
            "interfaceBuilderInspector.treeMenu.createComponentKind.data_source",
          ),
          run: () => create("data_source"),
        },
        {
          id: "create-component-form",
          label: t("interfaceBuilderInspector.treeMenu.createComponentKind.form"),
          run: () => create("form"),
        },
        {
          id: "create-component-search",
          label: t("interfaceBuilderInspector.treeMenu.createComponentKind.search"),
          run: () => create("search"),
        },
        {
          id: "create-component-table",
          label: t("interfaceBuilderInspector.treeMenu.createComponentKind.table"),
          run: () => create("table"),
        },
        {
          id: "create-component-list",
          label: t("interfaceBuilderInspector.treeMenu.createComponentKind.list"),
          run: () => create("list"),
        },
      ];
    };

    if (node.selection.kind === "windows") {
      return [
        {
          id: "create-component",
          label: t("interfaceBuilderInspector.treeMenu.createComponent"),
          searchable: true,
          children: createComponentKindMenuItems(),
        },
        ...withFirstSeparator(expansionMenuItems),
      ] satisfies readonly BuilderTreeMenuItem[];
    }

    if (node.selection.kind === "sidebar") {
      return [
        {
          id: "create-sidebar-item",
          label: t("interfaceBuilderInspector.treeMenu.createSidebarItem"),
          run: () => createSidebarItem(defaultSidebarItemName, {
            onSidebarItemOpened: handleOpenComponent,
          }),
        },
        {
          id: "import-sidebar-item",
          label: t("interfaceBuilderInspector.treeMenu.importSidebarItem"),
          run: handleImportSidebarItem,
        },
        ...withFirstSeparator(expansionMenuItems),
      ] satisfies readonly BuilderTreeMenuItem[];
    }

    if (node.selection.kind === "component") {
      const componentId = getTreeNodeActionComponentId(node);
      if (!componentId) return expansionMenuItems;

      const component = uiComponents.components.find(
        (currentComponent) => currentComponent.uuid === componentId,
      );
      if (!component) return expansionMenuItems;

      const isFocused = interfaceBuilderFocusComponentId === component.uuid;
      const transferTargets = projects.filter(
        (project) => project.uuid !== component.projectId,
      );
      return [
        {
          id: "create-component",
          label: t("interfaceBuilderInspector.treeMenu.createComponent"),
          searchable: true,
          children: createComponentKindMenuItems(component.uuid),
        },
        {
          id: "clone-component",
          label: t("interfaceBuilderInspector.treeMenu.cloneComponent"),
          run: () => cloneComponent(component),
        },
        ...(canGroupSelectedComponents && selectedNodeIdSet.has(node.id)
          ? [{
            id: "group-components",
            label: t("interfaceBuilderInspector.treeMenu.groupComponents"),
            run: handleGroupSelectedComponents,
          }]
          : []),
        // A component travels with everything under it, so a group is handed
        // over the same way a single component is. The project it is leaving is
        // its own rather than the visitor's: a page of the administration
        // project is read by every account, and copying one into the project
        // the visitor is signed into is the very thing this is for.
        ...(transferTargets.length > 0
          ? [
            {
              id: "copy-component-to-project",
              label: t(
                "interfaceBuilderInspector.treeMenu.copyComponentToProject",
              ),
              children: transferTargets.map((project) => ({
                id: `copy-component-to-project:${project.uuid}`,
                label: project.name || "—",
                run: () => copyComponentToProject(component, project.uuid),
              })),
            },
            // A component of another project -- the administration project,
            // whose pages every account reads -- is copied out but never taken
            // away: removing it is pinned to the project the visitor is signed
            // into, so the original would stay behind after the copy landed.
            // Neither is a component some menu item opens, which the move would
            // leave pointing at nothing.
            ...(component.projectId === session.projectId
              && !sidebarLinkedComponentIds.has(component.uuid)
              ? [{
                id: "move-component-to-project",
                label: t(
                  "interfaceBuilderInspector.treeMenu.moveComponentToProject",
                ),
                children: transferTargets.map((project) => ({
                  id: `move-component-to-project:${project.uuid}`,
                  label: project.name || "—",
                  run: () => handleMoveComponentToProject(
                    component,
                    project.uuid,
                  ),
                })),
              }]
              : []),
          ]
          : []),
        ...withFirstSeparator(expansionMenuItems),
        {
          id: "focus-component",
          label: isFocused
            ? t("interfaceBuilderInspector.treeMenu.unfocusComponent")
            : t("interfaceBuilderInspector.treeMenu.focusComponent"),
          run: () => setInterfaceBuilderFocusComponentId(
            isFocused ? null : component.uuid,
          ),
          separatorBefore: expansionMenuItems.length > 0,
        },
        createCopyIdTreeMenuItem(component.uuid, t, copyToClipboard),
        // Deleting from a node of the selection deletes the whole selection;
        // everywhere else it deletes the component the menu was opened on.
        ...(deletesSelection
          ? [{
            id: "delete-components",
            label: t("interfaceBuilderInspector.treeMenu.deleteComponents", {
              count: selectedComponents.length,
            }),
            run: () => openComponentDeleteConfirmation(selectedComponents),
            separatorBefore: true,
            variant: "danger" as const,
          }]
          : [{
            id: "delete-component",
            label: t("interfaceBuilderInspector.treeMenu.deleteComponent"),
            run: () => openComponentDeleteConfirmation([component]),
            separatorBefore: true,
            variant: "danger" as const,
          }]),
      ] satisfies readonly BuilderTreeMenuItem[];
    }

    if (node.selection.kind === "sidebarItem") {
      const { sidebarItemId } = node.selection;
      const sidebarItem = dynamicSidebarItems.items.find(
        (currentItem) => currentItem.uuid === sidebarItemId,
      );
      if (!sidebarItem) return [];

      return [
        createCopyIdTreeMenuItem(sidebarItem.uuid, t, copyToClipboard),
        {
          id: "clone-sidebar-item",
          label: t("interfaceBuilderInspector.treeMenu.cloneSidebarItem"),
          run: () => cloneSidebarItem(sidebarItem, {
            onSidebarItemOpened: handleOpenComponent,
          }),
        },
        {
          id: "export-sidebar-item",
          label: t("interfaceBuilderInspector.treeMenu.exportSidebarItem"),
          run: () => handleExportSidebarItem(sidebarItem),
        },
        {
          id: "delete-sidebar-item",
          label: t("interfaceBuilderInspector.treeMenu.deleteSidebarItem"),
          run: () => hideSidebarItem(sidebarItem, {
            onSidebarItemRemoved: handleSidebarItemRemoved,
          }),
          separatorBefore: true,
          variant: "danger",
        },
      ] satisfies readonly BuilderTreeMenuItem[];
    }

    return expansionMenuItems;
  }, [
    canGroupSelectedComponents,
    cloneComponent,
    cloneSidebarItem,
    copyComponentToProject,
    createComponent,
    createSidebarItem,
    copyToClipboard,
    dynamicSidebarItems.items,
    handleExportSidebarItem,
    handleGroupSelectedComponents,
    handleImportSidebarItem,
    handleMoveComponentToProject,
    handleOpenComponent,
    handleSidebarItemRemoved,
    hideSidebarItem,
    interfaceBuilderFocusComponentId,
    openComponentDeleteConfirmation,
    selectedComponents,
    selectedNodeIdSet,
    selectedNodeIds,
    setInterfaceBuilderFocusComponentId,
    session.projectId,
    projects,
    setTreeNodeSubtreeExpanded,
    sidebarLinkedComponentIds,
    t,
    uiComponents.components,
  ]);
  const bringInspectorToFront = useCallback((
    stackOrder = getNextFloatingWindowStackOrder(),
  ) => {
    setInspectorStackOrder(stackOrder);
    return stackOrder;
  }, []);
  const bringObjectEditorToFront = useCallback((index: number) => {
    const stackOrder = getNextFloatingWindowStackOrder();
    setInspectorStackOrder(stackOrder);
    setObjectEditorStack((currentStack) => currentStack.map((
      frame,
      frameIndex,
    ) => (
      frameIndex === index ? { ...frame, stackOrder } : frame
    )));
  }, []);
  const handleInspectorPointerDown = useCallback((
    event: ReactPointerEvent<HTMLElement>,
  ) => {
    if (event.button !== 0) return;

    bringInspectorToFront();
  }, [bringInspectorToFront]);
  const handleObjectEditorPointerDown = useCallback((
    index: number,
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    if (event.button !== 0) return;

    event.stopPropagation();
    bringObjectEditorToFront(index);
  }, [bringObjectEditorToFront]);
  const handlePropertyCommit = useCallback(
    async (
      property: BuilderProperty,
      value: BuilderPropertyValue,
    ): Promise<boolean> => {
      if (!selectedNode || !property.editable) return false;

      setPropertyDrafts((currentDrafts) => upsertPropertyDraft(
        currentDrafts,
        {
          property,
          selection: selectedNode.selection,
          selectionId: selectedNode.id,
          value,
        },
      ));
      if (selectedNode.selection.kind === "component") {
        const componentSelection = selectedNode.selection;
        setInterfaceBuilderComponentPropertyDrafts((currentDrafts) => (
          upsertComponentPropertyPreviewDraft(
            currentDrafts,
            {
              propertyPath: property.path,
              selection: componentSelection,
              selectionId: selectedNode.id,
              value,
            },
            property.value,
          )
        ));
      }
      return true;
    },
    [selectedNode, setInterfaceBuilderComponentPropertyDrafts],
  );
  const handlePropertyDraftCancel = useCallback(() => {
    setPropertyDrafts([]);
    setObjectEditorStack([]);
    setInterfaceBuilderComponentPropertyDrafts([]);
    setPropertyDraftResetVersion((version) => version + 1);
  }, [setInterfaceBuilderComponentPropertyDrafts]);
  const handlePropertyDraftSave = useCallback(async () => {
    if (propertyDrafts.length === 0 || propertySubmitting) return;

    const succeeded = await commitPropertyChanges(
      propertyDrafts.map((draft) => ({
        property: draft.property,
        selection: draft.selection,
        value: draft.value,
      })),
    );
    if (!succeeded) return;

    setPropertyDrafts([]);
    setObjectEditorStack([]);
    setInterfaceBuilderComponentPropertyDrafts([]);
    setPropertyDraftResetVersion((version) => version + 1);
  }, [
    commitPropertyChanges,
    propertyDrafts,
    propertySubmitting,
    setInterfaceBuilderComponentPropertyDrafts,
  ]);
  useEffect(() => {
    const handleBuilderSaveHotkey = (event: globalThis.KeyboardEvent) => {
      if (
        event.defaultPrevented
        || event.isComposing
        || !event.ctrlKey
        || event.metaKey
        || event.altKey
        || event.shiftKey
        || event.code !== "KeyS"
      ) {
        return;
      }

      event.preventDefault();
      void handlePropertyDraftSave();
    };

    window.addEventListener("keydown", handleBuilderSaveHotkey);
    return () => window.removeEventListener("keydown", handleBuilderSaveHotkey);
  }, [handlePropertyDraftSave]);
  const handleObjectPropertyOpen = useCallback((
    property: BuilderProperty,
    draftValue: BuilderPropertyValue = property.value,
  ) => {
    if (!selectedNode) return;

    const selectionId = selectedNode.id;
    const stackOrder = getNextFloatingWindowStackOrder();
    setInspectorStackOrder(stackOrder);
    setObjectEditorStack((currentStack) => {
      const existingIndex = currentStack.findIndex((frame) => (
        frame.selectionId === selectionId
        && arePropertyPathsEqual(frame.property.path, property.path)
      ));
      if (existingIndex >= 0) {
        return currentStack.map((frame, frameIndex) => (
          frameIndex === existingIndex ? { ...frame, stackOrder } : frame
        ));
      }

      const position = getCenteredObjectEditorPosition();

      return [
        ...currentStack,
        {
          draftValue: cloneBuilderPropertyValue(draftValue),
          left: position.left,
          positionReady: false,
          property,
          selectionId,
          stackOrder,
          top: position.top,
        },
      ];
    });
  }, [selectedNode]);
  const closeObjectPropertyEditor = useCallback((index: number) => {
    setObjectEditorStack((currentStack) => currentStack.slice(0, index));
  }, []);
  const updateObjectEditorDraft = useCallback(async (
    index: number,
    property: BuilderProperty,
    value: BuilderPropertyValue,
  ): Promise<boolean> => {
    setObjectEditorStack((currentStack) => currentStack.map((frame, frameIndex) => {
      if (frameIndex !== index) return frame;

      return {
        ...frame,
        draftValue: writeDraftValueAtPath(
          frame.draftValue,
          property.path.slice(frame.property.path.length),
          value,
        ),
      };
    }));
    return true;
  }, []);
  const updateObjectEditorKind = useCallback((
    index: number,
    value: string | null,
  ) => {
    setObjectEditorStack((currentStack) => currentStack.map((frame, frameIndex) => (
      frameIndex === index
        ? { ...frame, draftValue: value ? { kind: value } : null }
        : frame
    )));
  }, []);
  const commitObjectPropertyEditor = useCallback(async (index: number) => {
    const frame = objectEditorStack[index];
    if (!frame) return;

    if (index > 0) {
      const parentFrame = objectEditorStack[index - 1];
      if (!parentFrame) return;

      setObjectEditorStack((currentStack) => {
        const currentParentFrame = currentStack[index - 1];
        const currentFrame = currentStack[index];
        if (!currentParentFrame || !currentFrame) return currentStack;

        return [
          ...currentStack.slice(0, index - 1),
          {
            ...currentParentFrame,
            draftValue: writeDraftValueAtPath(
              currentParentFrame.draftValue,
              currentFrame.property.path.slice(
                currentParentFrame.property.path.length,
              ),
              currentFrame.draftValue,
            ),
          },
        ];
      });
      return;
    }

    const succeeded = await handlePropertyCommit(frame.property, frame.draftValue);
    if (succeeded) {
      setObjectEditorStack([]);
    }
  }, [
    handlePropertyCommit,
    objectEditorStack,
  ]);
  const handleObjectEditorHeaderPointerDown = useCallback((
    index: number,
    event: ReactPointerEvent<HTMLElement>,
  ) => {
    if (event.button !== 0) return;

    const target = event.target;
    if (
      target instanceof Element
      && target.closest("button, input, textarea, select")
    ) {
      return;
    }

    const dialog = event.currentTarget.closest(
      ".interface-builder-inspector__object-dialog",
    );
    if (!(dialog instanceof HTMLElement)) return;

    const rect = dialog.getBoundingClientRect();
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setObjectEditorDragState({
      index,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    });
  }, []);
  const updateObjectEditorPosition = useCallback((
    index: number,
    position: { left: number; top: number },
  ) => {
    setObjectEditorStack((currentStack) => currentStack.map((frame, frameIndex) => (
      frameIndex === index
        ? {
          ...frame,
          left: position.left,
          positionReady: true,
          top: position.top,
        }
        : frame
    )));
  }, []);
  const inspectorStyle = {
    "--ib-tree-h": `${windowState.treeHeight}px`,
    height: windowState.height,
    left: windowState.left,
    top: windowState.top,
    width: windowState.width,
    ...(inspectorStackOrder > 0
      ? { zIndex: `calc(var(--z-interface-builder-window) + ${inspectorStackOrder})` }
      : {}),
  } as CSSProperties;
  const errorMessage = (
    editError
    ?? manifestError
    ?? sidebarActionError
  );
  // A manifest is read here rather than by the platform, so that failure has
  // nothing to report beyond what it already says.
  const errorDetails = editError
    ? editErrorDetails
    : manifestError
      ? null
      : sidebarActionErrorDetails;
  const errorTitle = editError
    ? t("interfaceBuilderInspector.editErrorTitle")
    : t("interfaceBuilderInspector.actionErrorTitle");
  const closeErrorDialog = useCallback(() => {
    clearEditError();
    setManifestError(null);
    clearSidebarActionError();
  }, [
    clearEditError,
    clearSidebarActionError,
  ]);
  const closeComponentDeleteConfirmation = useCallback(() => {
    setComponentDeleteConfirmation(null);
  }, []);
  const confirmComponentDelete = useCallback(() => {
    if (!componentDeleteConfirmation) return;

    const { components } = componentDeleteConfirmation;
    setComponentDeleteConfirmation(null);
    void deleteComponents(components, {
      onComponentRemoved: handleComponentRemoved,
    });
  }, [
    componentDeleteConfirmation,
    deleteComponents,
    handleComponentRemoved,
  ]);

  const updateWindowState = useCallback((
    updater: (
      currentState: InterfaceBuilderInspectorWindowState,
    ) => InterfaceBuilderInspectorWindowState,
  ) => {
    setWindowState((currentState) => normalizeWindowState(
      updater(currentState),
    ));
  }, []);

  const toggleNodeExpanded = useCallback((nodeId: string) => {
    updateWindowState((currentState) => {
      const expanded = new Set(currentState.expandedNodeIds);
      if (expanded.has(nodeId)) {
        expanded.delete(nodeId);
      } else {
        expanded.add(nodeId);
      }

      return {
        ...currentState,
        expandedNodeIds: [...expanded],
      };
    });
  }, [updateWindowState]);

  const handleHeaderPointerDown = useCallback((
    event: ReactPointerEvent<HTMLElement>,
  ) => {
    if (event.button !== 0) return;

    const target = event.target;
    if (
      target instanceof Element
      && target.closest("button, input, textarea, select")
    ) {
      return;
    }

    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;

    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDragState({
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    });
  }, []);

  const handleSplitterPointerDown = useCallback((
    event: ReactPointerEvent<HTMLButtonElement>,
  ) => {
    if (event.button !== 0) return;

    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setSplitDragState({
      startTreeHeight: windowState.treeHeight,
      startY: event.clientY,
    });
  }, [windowState.treeHeight]);

  const handleResizePointerDown = useCallback((
    event: ReactPointerEvent<HTMLSpanElement>,
  ) => {
    if (event.button !== 0) return;

    const edge = event.currentTarget.dataset.edge as ResizeEdge | undefined;
    if (!edge) return;

    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const startState = windowState;
    const startX = event.clientX;
    const startY = event.clientY;
    const handlePointerMove = (pointerEvent: PointerEvent) => {
      setWindowState(resizeInspectorWindow(
        startState,
        edge,
        pointerEvent.clientX - startX,
        pointerEvent.clientY - startY,
      ));
    };
    const handlePointerUp = () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
  }, [windowState]);

  useEffect(() => {
    writeInterfaceBuilderInspectorWindowState(windowState);
  }, [windowState]);

  useEffect(() => {
    setObjectEditorStack((currentStack) => currentStack.filter(
      (frame) => frame.selectionId === selectedNode?.id,
    ));
  }, [selectedNode?.id]);

  useEffect(() => {
    if (!dragState) return;

    const handlePointerMove = (event: PointerEvent) => {
      updateWindowState((currentState) => ({
        ...currentState,
        left: event.clientX - dragState.offsetX,
        top: event.clientY - dragState.offsetY,
      }));
    };
    const handlePointerUp = () => setDragState(null);

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [dragState, updateWindowState]);

  useEffect(() => {
    if (!splitDragState) return;

    const handlePointerMove = (event: PointerEvent) => {
      updateWindowState((currentState) => ({
        ...currentState,
        treeHeight: splitDragState.startTreeHeight
          + event.clientY
          - splitDragState.startY,
      }));
    };
    const handlePointerUp = () => setSplitDragState(null);

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [splitDragState, updateWindowState]);

  useEffect(() => {
    if (!objectEditorDragState) return;

    const handlePointerMove = (event: PointerEvent) => {
      setObjectEditorStack((currentStack) => currentStack.map((frame, index) => {
        if (index !== objectEditorDragState.index) return frame;

        return {
          ...frame,
          left: Math.max(
            VIEWPORT_MARGIN,
            event.clientX - objectEditorDragState.offsetX,
          ),
          positionReady: true,
          top: Math.max(
            VIEWPORT_MARGIN,
            event.clientY - objectEditorDragState.offsetY,
          ),
        };
      }));
    };
    const handlePointerUp = () => setObjectEditorDragState(null);

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [objectEditorDragState]);

  useEffect(() => {
    const handleResize = () => {
      updateWindowState((currentState) => currentState);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [updateWindowState]);

  return (
    <aside
      className={[
        "interface-builder-inspector",
        dragState ? "interface-builder-inspector--dragging" : "",
      ].filter(Boolean).join(" ")}
      aria-label={t("interfaceBuilderInspector.label")}
      ref={rootRef}
      onPointerDown={handleInspectorPointerDown}
      style={inspectorStyle}
    >
      <header
        className="interface-builder-inspector__header"
        onPointerDown={handleHeaderPointerDown}
      >
        <h2 className="interface-builder-inspector__title">
          {t("interfaceBuilderInspector.label")}
        </h2>
        <button
          className="interface-builder-inspector__close"
          type="button"
          aria-label={t("interfaceBuilderInspector.close")}
          onClick={() => setInterfaceBuilderInspectorOpen(false)}
        />
      </header>

      <section
        className="interface-builder-inspector__section interface-builder-inspector__section--tree"
        aria-labelledby="interface-builder-inspector-tree-title"
      >
        <h3
          className="interface-builder-inspector__section-title"
          id="interface-builder-inspector-tree-title"
        >
          {t("interfaceBuilderInspector.treeTitle")}
        </h3>
        <ul className="interface-builder-inspector__tree">
          <BuilderTreeNodeView
            actionsDisabled={treeActionsDisabled}
            dirtyNodeIds={dirtyNodeIds}
            expandedNodeIds={expandedNodeIds}
            draggedComponentId={draggedTreeComponentId}
            getMenuItems={getTreeMenuItems}
            node={tree}
            selectedId={selectedNodeId}
            selectedIds={selectedNodeIdSet}
            onDragComponentChange={setDraggedTreeComponentId}
            onMoveComponent={handleTreeComponentMove}
            onRunMenuAction={runTreeMenuAction}
            onSelect={handleTreeNodeSelect}
            onToggleExpanded={toggleNodeExpanded}
            t={t}
          />
        </ul>
      </section>

      <button
        className="interface-builder-inspector__splitter"
        type="button"
        aria-label={t("interfaceBuilderInspector.resizeSections")}
        onPointerDown={handleSplitterPointerDown}
      />

      <section
        className="interface-builder-inspector__section interface-builder-inspector__section--properties"
        aria-labelledby="interface-builder-inspector-properties-title"
      >
        <div className="interface-builder-inspector__section-heading">
          <h3
            className="interface-builder-inspector__section-title"
            id="interface-builder-inspector-properties-title"
          >
            {t("interfaceBuilderInspector.propertiesTitle")}
          </h3>
          <div className="interface-builder-inspector__property-actions">
            <button
              aria-label={t("interfaceBuilderInspector.saveProperties")}
              className={[
                "interface-builder-inspector__property-action",
                "interface-builder-inspector__property-action--save",
              ].join(" ")}
              disabled={!hasPropertyDrafts || propertySubmitting}
              onClick={() => {
                void handlePropertyDraftSave();
              }}
              title={t("interfaceBuilderInspector.saveProperties")}
              type="button"
            >
              <PropertyDraftActionIcon name="save" />
            </button>
            <button
              aria-label={t("interfaceBuilderInspector.cancelProperties")}
              className={[
                "interface-builder-inspector__property-action",
                "interface-builder-inspector__property-action--cancel",
              ].join(" ")}
              disabled={!hasPropertyDrafts || propertySubmitting}
              onClick={handlePropertyDraftCancel}
              title={t("interfaceBuilderInspector.cancelProperties")}
              type="button"
            >
              <PropertyDraftActionIcon name="cancel" />
            </button>
          </div>
        </div>
        {selectedNode && selectedProperties.length > 0 ? (
          <form
            className="interface-builder-inspector__properties"
            onKeyDown={(event) => {
              if (
                event.key !== "Enter"
                || event.nativeEvent.isComposing
                || event.altKey
                || event.ctrlKey
                || event.metaKey
                || event.shiftKey
                || !(event.target instanceof HTMLInputElement)
                || selectedNode.selection.kind !== "component"
              ) {
                return;
              }

              event.preventDefault();
              void handlePropertyDraftSave();
            }}
            onSubmit={(event) => {
              event.preventDefault();
              void handlePropertyDraftSave();
            }}
          >
            <div className="interface-builder-inspector__property-list">
              {selectedProperties.map((property) => {
                const draft = findPropertyDraft(
                  selectedPropertyDrafts,
                  selectedNode.id,
                  property.path,
                );

                return (
                  <BuilderPropertyField
                    components={uiComponents.components}
                    disabled={propertySubmitting}
                    draftValue={draft?.value}
                    hasDraft={Boolean(draft)}
                    key={selectedNode.id + property.id}
                    onDraftChange={handlePropertyCommit}
                    onOpenObjectProperty={handleObjectPropertyOpen}
                    property={property}
                    resetVersion={propertyDraftResetVersion}
                    t={t}
                  />
                );
              })}
            </div>
          </form>
        ) : selectedNode ? (
          <p className="interface-builder-inspector__empty">
            {t("interfaceBuilderInspector.systemSelection")}
          </p>
        ) : (
          <p className="interface-builder-inspector__empty">
            {t("interfaceBuilderInspector.emptySelection")}
          </p>
        )}
      </section>
      {RESIZE_EDGES.map((edge) => (
        <span
          className="interface-builder-inspector__resize-handle"
          data-edge={edge}
          key={edge}
          onPointerDown={handleResizePointerDown}
        />
      ))}
      <input
        accept=".yaml,.yml"
        aria-label={t("interfaceBuilderInspector.treeMenu.importSidebarItem")}
        hidden
        onChange={(event) => void handleManifestFileChange(event)}
        ref={manifestInputRef}
        type="file"
      />
      {errorMessage ? (
        <BuilderPropertyErrorDialog
          details={errorDetails}
          message={errorMessage}
          onClose={closeErrorDialog}
          title={errorTitle}
          t={t}
        />
      ) : null}
      {componentDeleteConfirmation ? (
        <BuilderComponentDeleteDialog
          affectedComponents={componentDeleteConfirmation.affectedComponents}
          components={componentDeleteConfirmation.components}
          disabled={sidebarActionSubmitting}
          onCancel={closeComponentDeleteConfirmation}
          onConfirm={confirmComponentDelete}
          t={t}
        />
      ) : null}
      {selectedNode ? objectEditorStack.map((frame, index) => {
        if (frame.selectionId !== selectedNode.id) return null;
        const property = {
          ...frame.property,
          value: frame.draftValue,
        };
        const dialogProps = {
          components: uiComponents.components,
          depth: index,
          disabled: propertySubmitting,
          left: frame.left,
          onCancel: () => closeObjectPropertyEditor(index),
          onCommitDraft: (
            nestedProperty: BuilderProperty,
            value: BuilderPropertyValue,
          ) => updateObjectEditorDraft(index, nestedProperty, value),
          onHeaderPointerDown: (event: ReactPointerEvent<HTMLElement>) => (
            handleObjectEditorHeaderPointerDown(index, event)
          ),
          onOk: () => {
            void commitObjectPropertyEditor(index);
          },
          onOpenObjectProperty: handleObjectPropertyOpen,
          onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => (
            handleObjectEditorPointerDown(index, event)
          ),
          onPositionReady: (position: { left: number; top: number }) => (
            updateObjectEditorPosition(index, position)
          ),
          property,
          shouldCenter: !frame.positionReady,
          stackOrder: frame.stackOrder,
          t,
          top: frame.top,
        };
        const dialogKey = (
          `${frame.selectionId}:${frame.property.path.join(".")}:${index}`
        );

        if (property.type === "array" && property.itemFields) {
          return (
            <BuilderListPropertyDialog
              {...dialogProps}
              key={dialogKey}
              onItemsChange={(items) => (
                updateObjectEditorDraft(index, frame.property, items)
              )}
            />
          );
        }

        if (property.type !== "object") return null;

        return (
          <BuilderObjectPropertyDialog
            {...dialogProps}
            key={dialogKey}
            onKindChange={(value) => updateObjectEditorKind(index, value)}
          />
        );
      }) : null}
    </aside>
  );
}

function PropertyDraftActionIcon({ name }: { name: "cancel" | "save" }) {
  if (name === "save") {
    return (
      <svg
        aria-hidden="true"
        focusable="false"
        viewBox="0 0 16 16"
      >
        <path
          d="M3.2 8.1 6.5 11.4 13 4.6"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.8"
        />
      </svg>
    );
  }

  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 16 16"
    >
      <path
        d="M4.5 4.5 11.5 11.5M11.5 4.5 4.5 11.5"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function createBuilderPropertyClassName(
  property: BuilderProperty,
  dirty: boolean,
): string {
  return [
    "interface-builder-inspector__property",
    property.editable
      ? "interface-builder-inspector__property--editable"
      : "interface-builder-inspector__property--readonly",
    dirty ? "interface-builder-inspector__property--unsaved" : "",
  ].filter(Boolean).join(" ");
}

function BuilderPropertyField({
  components,
  disabled,
  draftValue,
  hasDraft = false,
  onDraftChange,
  onOpenObjectProperty,
  property,
  resetVersion = 0,
  t,
}: {
  components: readonly StoredUiComponent[];
  disabled: boolean;
  draftValue?: BuilderPropertyValue;
  hasDraft?: boolean;
  onDraftChange: (
    property: BuilderProperty,
    value: BuilderPropertyValue,
  ) => Promise<boolean> | boolean;
  onOpenObjectProperty: (
    property: BuilderProperty,
    draftValue?: BuilderPropertyValue,
  ) => void;
  property: BuilderProperty;
  resetVersion?: number;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const effectiveValue = hasDraft ? draftValue ?? null : property.value;
  const currentProperty = {
    ...property,
    value: effectiveValue,
  };
  const committedTextValue = formatBuilderPropertyTextValue(
    currentProperty,
    effectiveValue,
  );
  const inputId = useId();
  const [textValue, setTextValue] = useState(committedTextValue);
  const editable = property.editable && !disabled;
  const selectValue = effectiveValue === null
    ? NULL_SELECT_VALUE
    : String(effectiveValue);
  const textDraftDirty = textValue !== committedTextValue;
  const propertyDirty = hasDraft || textDraftDirty;
  const propertyClassName = createBuilderPropertyClassName(property, propertyDirty);
  const textInputClassName = [
    "interface-builder-inspector__property-input",
    propertyDirty
      ? "interface-builder-inspector__property-input--unsaved"
      : "",
  ].filter(Boolean).join(" ");

  useEffect(() => {
    setTextValue(committedTextValue);
  }, [
    committedTextValue,
    property.id,
    resetVersion,
  ]);

  const updateTextDraft = useCallback((nextValue: string) => {
    setTextValue(nextValue);

    if (disabled) return;

    const draft = parseBuilderPropertyTextDraft(property, nextValue);
    if (!draft.valid) {
      void onDraftChange(property, property.value);
      return;
    }

    void onDraftChange(property, draft.value);
  }, [
    disabled,
    onDraftChange,
    property,
  ]);
  const handleTextChange = useCallback((
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    updateTextDraft(event.currentTarget.value);
  }, [updateTextDraft]);

  const handleNumberStep = useCallback((direction: -1 | 1) => {
    if (!editable) return;

    const parsedTextValue = Number(textValue);
    const parsedPropertyValue = Number(effectiveValue);
    const nextValue = Number.isFinite(parsedTextValue)
      ? parsedTextValue
      : (Number.isFinite(parsedPropertyValue) ? parsedPropertyValue : 0);

    updateTextDraft(String(nextValue + direction));
  }, [
    effectiveValue,
    editable,
    textValue,
    updateTextDraft,
  ]);

  const handleCheckboxChange = useCallback((
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    if (disabled) return;

    void onDraftChange(property, event.currentTarget.checked);
  }, [disabled, onDraftChange, property]);

  const handleSelectChange = useCallback((
    event: ChangeEvent<HTMLSelectElement>,
  ) => {
    if (disabled) return;

    const value = event.currentTarget.value === NULL_SELECT_VALUE
      ? null
      : event.currentTarget.value;
    void onDraftChange(property, value);
  }, [disabled, onDraftChange, property]);

  const selectOptions = property.options ?? [];
  const componentReferenceOptions = property.componentKind
    ? createComponentReferenceOptions(components, property.componentKind)
    : [];

  if (property.type === "array" && property.itemFields) {
    return (
      <div
        className={propertyClassName}
      >
        <span className="interface-builder-inspector__property-label">
          {property.label}
        </span>
        <button
          aria-label={t("interfaceBuilderInspector.openListPropertyEditor")}
          className="interface-builder-inspector__property-object-button"
          disabled={disabled}
          onClick={() => onOpenObjectProperty(
            property,
            normalizeArrayPropertyValue(currentProperty),
          )}
          style={{
            ...PROPERTY_OBJECT_BUTTON_STYLE,
            ...(disabled ? PROPERTY_OBJECT_BUTTON_DISABLED_STYLE : null),
          }}
          type="button"
        >
          <span style={PROPERTY_OBJECT_SUMMARY_STYLE}>
            {t("interfaceBuilderInspector.listPropertySummary", {
              total: Array.isArray(effectiveValue) ? effectiveValue.length : 0,
            })}
          </span>
          <span
            aria-hidden="true"
            style={PROPERTY_OBJECT_AFFORDANCE_STYLE}
          >
            ...
          </span>
        </button>
      </div>
    );
  }

  if (property.type === "object") {
    return (
      <div
        className={propertyClassName}
      >
        <span className="interface-builder-inspector__property-label">
          {property.label}
        </span>
        <button
          aria-label={t("interfaceBuilderInspector.openPropertyEditor")}
          className="interface-builder-inspector__property-object-button"
          disabled={disabled}
          onClick={() => onOpenObjectProperty(property, effectiveValue)}
          style={{
            ...PROPERTY_OBJECT_BUTTON_STYLE,
            ...(disabled ? PROPERTY_OBJECT_BUTTON_DISABLED_STYLE : null),
          }}
          type="button"
        >
          <span style={PROPERTY_OBJECT_SUMMARY_STYLE}>
            {formatObjectPropertySummary(currentProperty, t)}
          </span>
          <span
            aria-hidden="true"
            style={PROPERTY_OBJECT_AFFORDANCE_STYLE}
          >
            ...
          </span>
        </button>
      </div>
    );
  }

  if (
    property.type === "number"
    && selectOptions.length === 0
    && !property.componentKind
  ) {
    return (
      <div
        className={propertyClassName}
      >
        <label
          className="interface-builder-inspector__property-label"
          htmlFor={inputId}
        >
          {property.label}
        </label>
        <span className="interface-builder-inspector__property-number-control">
          <input
            className={[
              textInputClassName,
              "interface-builder-inspector__property-number-input",
            ].join(" ")}
            id={inputId}
            inputMode="decimal"
            onChange={handleTextChange}
            readOnly={!editable}
            // An unset nullable property is shown and cleared as the literal
            // `null` (see parseBuilderPropertyTextDraft). A number input rejects
            // that text: the browser drops the value, leaving the field blank
            // with no way to type `null` back in. Nullable numbers therefore use
            // a text input, keeping the decimal keypad through inputMode.
            type={property.nullable ? "text" : "number"}
            value={textValue}
          />
          <span className="interface-builder-inspector__property-number-stepper">
            <button
              aria-label={t("interfaceBuilderInspector.numberIncrement", {
                name: property.label,
              })}
              className={[
                "interface-builder-inspector__property-number-step",
                "interface-builder-inspector__property-number-step--increment",
              ].join(" ")}
              disabled={!editable}
              onClick={() => handleNumberStep(1)}
              type="button"
            />
            <button
              aria-label={t("interfaceBuilderInspector.numberDecrement", {
                name: property.label,
              })}
              className={[
                "interface-builder-inspector__property-number-step",
                "interface-builder-inspector__property-number-step--decrement",
              ].join(" ")}
              disabled={!editable}
              onClick={() => handleNumberStep(-1)}
              type="button"
            />
          </span>
        </span>
      </div>
    );
  }

  if (
    property.color
    && property.type === "string"
    && selectOptions.length === 0
    && !property.componentKind
  ) {
    return (
      <div
        className={propertyClassName}
      >
        <label
          className="interface-builder-inspector__property-label"
          htmlFor={inputId}
        >
          {property.label}
        </label>
        <span className="interface-builder-inspector__property-color-control">
          <input
            className={textInputClassName}
            id={inputId}
            onChange={handleTextChange}
            readOnly={!editable}
            type="text"
            value={textValue}
          />
          <input
            aria-label={t("interfaceBuilderInspector.propertyColorPicker", {
              name: property.label,
            })}
            className="interface-builder-inspector__property-color-picker"
            disabled={!editable}
            onChange={handleTextChange}
            type="color"
            value={toColorInputValue(textValue)}
          />
        </span>
      </div>
    );
  }

  return (
    <label
      className={propertyClassName}
    >
      <span className="interface-builder-inspector__property-label">
        {property.label}
      </span>
      {property.type === "boolean" ? (
        <span className="interface-builder-inspector__property-control">
          <input
            checked={effectiveValue === true}
            disabled={!editable}
            onChange={handleCheckboxChange}
            type="checkbox"
          />
        </span>
      ) : selectOptions.length > 0 ? (
        <span className="interface-builder-inspector__property-select-control">
          <select
            className="interface-builder-inspector__property-select"
            disabled={!editable}
            onChange={handleSelectChange}
            value={selectValue}
          >
            {property.nullable ? (
              <option value={NULL_SELECT_VALUE}>
                {t("interfaceBuilderInspector.propertyOptionNone")}
              </option>
            ) : null}
            {selectOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </span>
      ) : property.componentKind ? (
        <span className="interface-builder-inspector__property-select-control">
          <select
            className="interface-builder-inspector__property-select"
            disabled={!editable}
            onChange={handleSelectChange}
            value={selectValue}
          >
            {property.nullable ? (
              <option value={NULL_SELECT_VALUE}>
                {t("interfaceBuilderInspector.propertyOptionNone")}
              </option>
            ) : null}
            {componentReferenceOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
            {typeof effectiveValue === "string"
              && effectiveValue
              && !componentReferenceOptions.some((option) => (
                option.value === effectiveValue
              )) ? (
                <option value={effectiveValue}>{effectiveValue}</option>
              ) : null}
          </select>
        </span>
      ) : property.multiline ? (
        <textarea
          className={[
            textInputClassName,
            "interface-builder-inspector__property-textarea",
          ].join(" ")}
          onChange={handleTextChange}
          readOnly={!editable}
          rows={4}
          value={textValue}
        />
      ) : (
        <input
          className={textInputClassName}
          onChange={handleTextChange}
          readOnly={!editable}
          type="text"
          value={textValue}
        />
      )}
    </label>
  );
}

interface BuilderPropertyDialogProps {
  components: readonly StoredUiComponent[];
  depth: number;
  disabled: boolean;
  left: number;
  onCancel: () => void;
  onCommitDraft: (
    property: BuilderProperty,
    value: BuilderPropertyValue,
  ) => Promise<boolean>;
  onHeaderPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onOk: () => void;
  onOpenObjectProperty: (
    property: BuilderProperty,
    draftValue?: BuilderPropertyValue,
  ) => void;
  onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onPositionReady: (position: { left: number; top: number }) => void;
  property: BuilderProperty;
  shouldCenter: boolean;
  stackOrder: number;
  t: (key: string, options?: Record<string, unknown>) => string;
  top: number;
}

// The chrome shared by every property editor dialog: the draggable header, the
// scrolling body, and the confirm / cancel footer. Only the body differs
// between an object editor and a list editor.
function BuilderPropertyDialogShell({
  children,
  depth,
  left,
  okDisabled,
  onCancel,
  onHeaderPointerDown,
  onOk,
  onPointerDown,
  onPositionReady,
  shouldCenter,
  stackOrder,
  t,
  title,
  top,
}: {
  children: ReactNode;
  depth: number;
  left: number;
  okDisabled: boolean;
  onCancel: () => void;
  onHeaderPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onOk: () => void;
  onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onPositionReady: (position: { left: number; top: number }) => void;
  shouldCenter: boolean;
  stackOrder: number;
  t: (key: string, options?: Record<string, unknown>) => string;
  title: string;
  top: number;
}) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const titleId = `interface-builder-inspector-object-dialog-title-${depth}`;
  const dialogStyle = {
    left,
    maxHeight: "calc(100vh - 120px)",
    minWidth: 280,
    overflow: "hidden",
    padding: 0,
    position: "fixed",
    borderRadius: "var(--radius-lg)",
    display: "grid",
    gridTemplateRows: "34px minmax(0, 1fr) auto",
    top,
    width: "min(460px, calc(100vw - var(--space-6)))",
    zIndex: `calc(var(--z-dropdown) + ${stackOrder})`,
  } as CSSProperties;

  useLayoutEffect(() => {
    if (!shouldCenter) return;

    const dialog = dialogRef.current;
    if (!dialog) return;

    const rect = dialog.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    onPositionReady(getCenteredObjectEditorPosition(
      rect.width,
      rect.height,
    ));
  }, [
    onPositionReady,
    shouldCenter,
  ]);

  return (
    <div
      aria-labelledby={titleId}
      className="interface-builder-inspector__dialog interface-builder-inspector__object-dialog"
      onPointerDown={onPointerDown}
      ref={dialogRef}
      role="dialog"
      style={dialogStyle}
    >
      <header
        className="interface-builder-inspector__header interface-builder-inspector__object-dialog-header"
        onPointerDown={onHeaderPointerDown}
      >
        <h3 className="interface-builder-inspector__title" id={titleId}>
          {title}
        </h3>
        <button
          aria-label={t("interfaceBuilderInspector.closePropertyEditor")}
          className="interface-builder-inspector__close"
          onClick={onCancel}
          onPointerDown={(event) => event.stopPropagation()}
          type="button"
        />
      </header>
      <div className="interface-builder-inspector__property-list interface-builder-inspector__object-property-list">
        {children}
      </div>
      <footer style={OBJECT_DIALOG_FOOTER_STYLE}>
        <button
          className="interface-builder-inspector__dialog-button"
          onClick={onCancel}
          style={OBJECT_DIALOG_BUTTON_STYLE}
          type="button"
        >
          {t("interfaceBuilderInspector.cancelPropertyEditor")}
        </button>
        <button
          className="interface-builder-inspector__dialog-button"
          disabled={okDisabled}
          onClick={onOk}
          style={OBJECT_DIALOG_BUTTON_STYLE}
          type="button"
        >
          {t("interfaceBuilderInspector.confirmPropertyEditor")}
        </button>
      </footer>
    </div>
  );
}

function BuilderObjectPropertyDialog({
  components,
  depth,
  disabled,
  left,
  onCancel,
  onCommitDraft,
  onHeaderPointerDown,
  onKindChange,
  onOk,
  onOpenObjectProperty,
  onPointerDown,
  onPositionReady,
  property,
  shouldCenter,
  stackOrder,
  t,
  top,
}: BuilderPropertyDialogProps & {
  onKindChange: (value: string | null) => void;
}) {
  const nestedProperties = createObjectBuilderProperties(property);
  const objectKind = readObjectPropertyKind(property);
  const kindOptions = property.options ?? Object.keys(property.variants ?? {});
  const kindSelectValue = objectKind
    ?? (
      property.nullable
        ? NULL_SELECT_VALUE
        : kindOptions[0] ?? NULL_SELECT_VALUE
    );

  const handleKindChange = useCallback((
    event: ChangeEvent<HTMLSelectElement>,
  ) => {
    if (disabled) return;

    const value = event.currentTarget.value === NULL_SELECT_VALUE
      ? null
      : event.currentTarget.value;
    onKindChange(value);
  }, [
    disabled,
    onKindChange,
  ]);

  return (
    <BuilderPropertyDialogShell
      depth={depth}
      left={left}
      okDisabled={disabled || !property.editable}
      onCancel={onCancel}
      onHeaderPointerDown={onHeaderPointerDown}
      onOk={onOk}
      onPointerDown={onPointerDown}
      onPositionReady={onPositionReady}
      shouldCenter={shouldCenter}
      stackOrder={stackOrder}
      t={t}
      title={property.label}
      top={top}
    >
      {kindOptions.length > 0 ? (
        <label className="interface-builder-inspector__property interface-builder-inspector__property--editable">
          <span className="interface-builder-inspector__property-label">
            {t("interfaceBuilderInspector.propertyKindLabel")}
          </span>
          <span className="interface-builder-inspector__property-select-control">
            <select
              className="interface-builder-inspector__property-select"
              disabled={!property.editable || disabled}
              onChange={handleKindChange}
              value={kindSelectValue}
            >
              {property.nullable ? (
                <option value={NULL_SELECT_VALUE}>
                  {t("interfaceBuilderInspector.propertyOptionNone")}
                </option>
              ) : null}
              {kindOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </span>
        </label>
      ) : null}
      {nestedProperties.map((nestedProperty) => (
        <BuilderPropertyField
          components={components}
          disabled={disabled}
          key={nestedProperty.path.join(".")}
          onDraftChange={onCommitDraft}
          onOpenObjectProperty={onOpenObjectProperty}
          property={nestedProperty}
          t={t}
        />
      ))}
      {nestedProperties.length === 0 ? (
        <p className="interface-builder-inspector__empty">
          {t("interfaceBuilderInspector.objectPropertyEmpty")}
        </p>
      ) : null}
    </BuilderPropertyDialogShell>
  );
}

// A list property is edited row by row: every item is described by the item
// spec, so its fields are the same controls the properties panel uses, and the
// row buttons cover what JSON text made tedious — adding, dropping, and
// reordering items.
function BuilderListPropertyDialog({
  components,
  depth,
  disabled,
  left,
  onCancel,
  onCommitDraft,
  onHeaderPointerDown,
  onItemsChange,
  onOk,
  onOpenObjectProperty,
  onPointerDown,
  onPositionReady,
  property,
  shouldCenter,
  stackOrder,
  t,
  top,
}: BuilderPropertyDialogProps & {
  onItemsChange: (items: unknown[]) => void;
}) {
  const items = Array.isArray(property.value) ? property.value : [];
  const editable = property.editable && !disabled;

  return (
    <BuilderPropertyDialogShell
      depth={depth}
      left={left}
      okDisabled={disabled || !property.editable}
      onCancel={onCancel}
      onHeaderPointerDown={onHeaderPointerDown}
      onOk={onOk}
      onPointerDown={onPointerDown}
      onPositionReady={onPositionReady}
      shouldCenter={shouldCenter}
      stackOrder={stackOrder}
      t={t}
      title={property.label}
      top={top}
    >
      {items.map((_, index) => (
        <div
          className="interface-builder-inspector__list-item"
          key={index}
        >
          <div className="interface-builder-inspector__list-item-header">
            <span className="interface-builder-inspector__list-item-title">
              {t("interfaceBuilderInspector.listItemLabel", {
                index: index + 1,
              })}
            </span>
            <span className="interface-builder-inspector__property-actions">
              <button
                aria-label={t("interfaceBuilderInspector.moveListItemUp", {
                  index: index + 1,
                })}
                className="interface-builder-inspector__property-action"
                disabled={!editable || index === 0}
                onClick={() => onItemsChange(moveListItem(items, index, -1))}
                title={t("interfaceBuilderInspector.moveListItemUp", {
                  index: index + 1,
                })}
                type="button"
              >
                <ListItemActionIcon name="up" />
              </button>
              <button
                aria-label={t("interfaceBuilderInspector.moveListItemDown", {
                  index: index + 1,
                })}
                className="interface-builder-inspector__property-action"
                disabled={!editable || index === items.length - 1}
                onClick={() => onItemsChange(moveListItem(items, index, 1))}
                title={t("interfaceBuilderInspector.moveListItemDown", {
                  index: index + 1,
                })}
                type="button"
              >
                <ListItemActionIcon name="down" />
              </button>
              <button
                aria-label={t("interfaceBuilderInspector.removeListItem", {
                  index: index + 1,
                })}
                className="interface-builder-inspector__property-action interface-builder-inspector__property-action--cancel"
                disabled={!editable}
                onClick={() => onItemsChange(
                  items.filter((_item, itemIndex) => itemIndex !== index),
                )}
                title={t("interfaceBuilderInspector.removeListItem", {
                  index: index + 1,
                })}
                type="button"
              >
                <ListItemActionIcon name="remove" />
              </button>
            </span>
          </div>
          {createArrayItemBuilderProperties(property, index).map((
            itemProperty,
          ) => (
            <BuilderPropertyField
              components={components}
              disabled={disabled}
              key={itemProperty.path.join(".")}
              onDraftChange={onCommitDraft}
              onOpenObjectProperty={onOpenObjectProperty}
              property={itemProperty}
              t={t}
            />
          ))}
        </div>
      ))}
      {items.length === 0 ? (
        <p className="interface-builder-inspector__empty">
          {t("interfaceBuilderInspector.listPropertyEmpty")}
        </p>
      ) : null}
      <button
        className="interface-builder-inspector__dialog-button interface-builder-inspector__dialog-button--secondary interface-builder-inspector__list-add"
        disabled={!editable}
        onClick={() => onItemsChange([...items, {}])}
        type="button"
      >
        {t("interfaceBuilderInspector.addListItem")}
      </button>
    </BuilderPropertyDialogShell>
  );
}

function ListItemActionIcon({ name }: { name: "down" | "remove" | "up" }) {
  if (name === "remove") {
    return (
      <svg aria-hidden="true" viewBox="0 0 16 16">
        <path
          d="M4 4l8 8M12 4l-8 8"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="1.6"
        />
      </svg>
    );
  }

  return (
    <svg aria-hidden="true" viewBox="0 0 16 16">
      <path
        d={name === "up" ? "M4 10l4-4 4 4" : "M4 6l4 4 4-4"}
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.6"
      />
    </svg>
  );
}

function moveListItem(
  items: readonly unknown[],
  index: number,
  direction: -1 | 1,
): unknown[] {
  const target = index + direction;
  if (target < 0 || target >= items.length) return [...items];

  const next = [...items];
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
}

type ParsedBuilderPropertyDraft =
  | { valid: true; value: BuilderPropertyValue }
  | { valid: false };

function formatBuilderPropertyTextValue(
  property: BuilderProperty,
  value: BuilderPropertyValue,
): string {
  if (property.type !== "array") return String(value);

  try {
    return JSON.stringify(Array.isArray(value) ? value : []);
  } catch {
    return "[]";
  }
}

// `<input type="color">` accepts only `#rrggbb`, while a color property holds
// free-form CSS (`transparent`, `var(--color-link)`, an empty value). What the
// picker cannot show falls back to black; such values are still typed by hand.
function toColorInputValue(value: string): string {
  const trimmed = value.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(trimmed)) return trimmed;
  if (/^#[0-9a-f]{3}$/.test(trimmed)) {
    return `#${[...trimmed.slice(1)].map((digit) => digit + digit).join("")}`;
  }

  return "#000000";
}

function parseBuilderPropertyTextDraft(
  property: BuilderProperty,
  draft: string,
): ParsedBuilderPropertyDraft {
  const trimmedDraft = draft.trim();
  if (property.nullable && trimmedDraft === "null") {
    return { valid: true, value: null };
  }

  if (property.type === "number") {
    if (!trimmedDraft) return { valid: false };

    const parsedNumber = Number(draft);
    if (!Number.isFinite(parsedNumber)) return { valid: false };

    return { valid: true, value: parsedNumber };
  }

  if (property.type === "array") {
    if (!trimmedDraft) return { valid: true, value: [] };

    try {
      const parsed = JSON.parse(trimmedDraft);
      return Array.isArray(parsed)
        ? { valid: true, value: parsed }
        : { valid: false };
    } catch {
      return { valid: false };
    }
  }

  return { valid: true, value: draft };
}

interface ComponentReferenceOption {
  label: string;
  value: string;
}

function createComponentReferenceOptions(
  components: readonly StoredUiComponent[],
  componentKind: string,
): ComponentReferenceOption[] {
  return components
    .filter((component) => componentMatchesKind(component, componentKind))
    .map((component) => ({
      label: `${component.name || component.uuid} (${component.uuid})`,
      value: component.uuid,
    }));
}

function componentMatchesKind(
  component: StoredUiComponent,
  componentKind: string,
): boolean {
  if (componentKind === "ui_component") return true;
  // A sidebar item opens a whole page, so it may only reference a page root:
  // a component with no parent of its own.
  if (componentKind === "ui_root_component") return component.parentUuid === null;

  // A property open to several kinds names them all, separated by spaces: a
  // search filters "table list", whichever of the two draws the collection.
  const kinds = componentKind.split(/\s+/).filter(Boolean);
  const scheme = component.uiScheme;
  const schemeKind = isRecord(scheme) ? readString(scheme.kind) : null;

  return kinds.some((kind) => (
    component.componentType === kind || schemeKind === kind
  ));
}

function arePropertyPathsEqual(
  left: readonly string[],
  right: readonly string[],
): boolean {
  return left.length === right.length
    && left.every((segment, index) => segment === right[index]);
}

function findPropertyDraft(
  drafts: readonly PropertyDraft[],
  selectionId: string,
  propertyPath: readonly string[],
): PropertyDraft | null {
  return drafts.find((draft) => (
    draft.selectionId === selectionId
    && arePropertyPathsEqual(draft.property.path, propertyPath)
  )) ?? null;
}

function upsertPropertyDraft(
  drafts: readonly PropertyDraft[],
  draft: PropertyDraft,
): readonly PropertyDraft[] {
  const nextDrafts = drafts.filter((currentDraft) => (
    currentDraft.selectionId !== draft.selectionId
    || !arePropertyPathsEqual(currentDraft.property.path, draft.property.path)
  ));

  if (areBuilderPropertyValuesEqual(draft.property.value, draft.value)) {
    return nextDrafts;
  }

  return [
    ...nextDrafts,
    {
      ...draft,
      value: cloneBuilderPropertyValue(draft.value),
    },
  ];
}

function areBuilderPropertyValuesEqual(
  left: BuilderPropertyValue,
  right: BuilderPropertyValue,
): boolean {
  return areUnknownValuesEqual(left, right);
}

function areUnknownValuesEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;

  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right)) return false;
    return left.length === right.length
      && left.every((item, index) => areUnknownValuesEqual(item, right[index]));
  }

  if (!isRecord(left) || !isRecord(right)) return false;

  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;

  return leftKeys.every((key) => (
    Object.prototype.hasOwnProperty.call(right, key)
    && areUnknownValuesEqual(left[key], right[key])
  ));
}

function formatObjectPropertySummary(
  property: BuilderProperty,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  return readObjectPropertyKind(property)
    ?? t("interfaceBuilderInspector.objectPropertySummaryEmpty");
}

function readObjectPropertyKind(property: BuilderProperty): string | null {
  return isRecord(property.value) ? readString(property.value.kind) : null;
}

function cloneBuilderPropertyValue(
  value: BuilderPropertyValue,
): BuilderPropertyValue {
  if (Array.isArray(value)) return cloneArray(value);
  if (!isRecord(value)) return value;

  return cloneRecord(value);
}

function cloneArray(value: unknown[]): unknown[] {
  return value.map((item) => {
    if (Array.isArray(item)) return cloneArray(item);
    if (isRecord(item)) return cloneRecord(item);
    return item;
  });
}

function cloneRecord(value: Record<string, unknown>): Record<string, unknown> {
  return Object.entries(value).reduce<Record<string, unknown>>(
    (draft, [key, fieldValue]) => {
      if (Array.isArray(fieldValue)) {
        draft[key] = cloneArray(fieldValue);
      } else {
        draft[key] = isRecord(fieldValue)
          ? cloneRecord(fieldValue)
          : fieldValue;
      }
      return draft;
    },
    {},
  );
}

function writeDraftValueAtPath(
  draftValue: BuilderPropertyValue,
  path: readonly string[],
  value: BuilderPropertyValue,
): BuilderPropertyValue {
  if (path.length === 0) return cloneBuilderPropertyValue(value);

  const [head, ...tail] = path;
  if (Array.isArray(draftValue)) {
    // List items are addressed by their index, so a numeric segment walks into
    // the array instead of turning it into a record.
    const index = Number(head);
    if (!Number.isInteger(index) || index < 0 || index >= draftValue.length) {
      return draftValue;
    }

    return draftValue.map((item, itemIndex) => (
      itemIndex === index
        ? writeDraftValueAtPath(item as BuilderPropertyValue, tail, value)
        : item
    ));
  }

  const draftRecord = isRecord(draftValue) ? draftValue : {};
  const currentValue = draftRecord[head];

  return {
    ...draftRecord,
    [head]: writeDraftValueAtPath(
      isRecord(currentValue) ? currentValue : {},
      tail,
      value,
    ),
  };
}

function BuilderComponentDeleteDialog({
  affectedComponents,
  components,
  disabled,
  onCancel,
  onConfirm,
  t,
}: {
  affectedComponents: readonly StoredUiComponent[];
  components: readonly StoredUiComponent[];
  disabled: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const componentIds = new Set(
    components.map((currentComponent) => currentComponent.uuid),
  );
  const nestedComponents = affectedComponents.filter(
    (currentComponent) => !componentIds.has(currentComponent.uuid),
  );
  // One component names itself; a selection is named by its size, and the list
  // below spells out what goes with it either way.
  const single = components.length === 1 ? components[0] : null;

  return (
    <div className="interface-builder-inspector__dialog-backdrop">
      <div
        aria-describedby="interface-builder-inspector-delete-component-description"
        aria-labelledby="interface-builder-inspector-delete-component-title"
        aria-modal="true"
        className="interface-builder-inspector__dialog"
        role="alertdialog"
        onKeyDown={(event) => {
          if (event.key !== "Enter" || disabled) return;

          event.preventDefault();
          onConfirm();
        }}
      >
        <h3
          className="interface-builder-inspector__dialog-title"
          id="interface-builder-inspector-delete-component-title"
        >
          {single
            ? t("interfaceBuilderInspector.deleteComponentDialog.title")
            : t("interfaceBuilderInspector.deleteComponentDialog.titleMany")}
        </h3>
        <p
          className="interface-builder-inspector__dialog-message"
          id="interface-builder-inspector-delete-component-description"
        >
          {single
            ? t("interfaceBuilderInspector.deleteComponentDialog.message", {
              name: single.name,
            })
            : t("interfaceBuilderInspector.deleteComponentDialog.messageMany", {
              count: components.length,
            })}
        </p>
        {single ? (
          <dl className="interface-builder-inspector__dialog-details">
            <div className="interface-builder-inspector__dialog-detail">
              <dt>{t("interfaceBuilderInspector.deleteComponentDialog.nameLabel")}</dt>
              <dd>{single.name}</dd>
            </div>
            <div className="interface-builder-inspector__dialog-detail">
              <dt>{t("interfaceBuilderInspector.deleteComponentDialog.uuidLabel")}</dt>
              <dd>{single.uuid}</dd>
            </div>
          </dl>
        ) : null}
        {nestedComponents.length > 0 || !single ? (
          <section className="interface-builder-inspector__dialog-component-impact">
            <h4 className="interface-builder-inspector__dialog-subtitle">
              {t("interfaceBuilderInspector.deleteComponentDialog.affectedTitle")}
            </h4>
            <ul className="interface-builder-inspector__dialog-component-list">
              {affectedComponents.map((currentComponent) => (
                <li
                  className="interface-builder-inspector__dialog-component-list-item"
                  key={currentComponent.uuid}
                >
                  <span className="interface-builder-inspector__dialog-component-name">
                    {currentComponent.name}
                  </span>
                  <span className="interface-builder-inspector__dialog-component-uuid">
                    {currentComponent.uuid}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        <div className="interface-builder-inspector__dialog-actions">
          <button
            autoFocus
            className={[
              "interface-builder-inspector__dialog-button",
              "interface-builder-inspector__dialog-button--secondary",
            ].join(" ")}
            disabled={disabled}
            onClick={onCancel}
            type="button"
          >
            {t("interfaceBuilderInspector.deleteComponentDialog.cancel")}
          </button>
          <button
            className={[
              "interface-builder-inspector__dialog-button",
              "interface-builder-inspector__dialog-button--danger",
            ].join(" ")}
            disabled={disabled}
            onClick={onConfirm}
            type="button"
          >
            {t("interfaceBuilderInspector.deleteComponentDialog.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

function BuilderPropertyErrorDialog({
  details,
  message,
  onClose,
  title,
  t,
}: {
  details?: string | null;
  message: string;
  onClose: () => void;
  title: string;
  t: (key: string) => string;
}) {
  return (
    <div className="interface-builder-inspector__dialog-backdrop">
      <div
        aria-labelledby="interface-builder-inspector-edit-error-title"
        aria-modal="true"
        className="interface-builder-inspector__dialog"
        role="alertdialog"
      >
        <h3
          className="interface-builder-inspector__dialog-title"
          id="interface-builder-inspector-edit-error-title"
        >
          {title}
        </h3>
        <p className="interface-builder-inspector__dialog-message">
          {message}
        </p>
        {details ? (
          <details className="interface-builder-inspector__dialog-report">
            <summary
              className="interface-builder-inspector__dialog-report-summary"
            >
              {t("interfaceBuilderInspector.errorDetails")}
            </summary>
            <p className="interface-builder-inspector__dialog-report-text">
              {details}
            </p>
          </details>
        ) : null}
        <button
          autoFocus
          className="interface-builder-inspector__dialog-button"
          onClick={onClose}
          type="button"
        >
          {t("interfaceBuilderInspector.editErrorDismiss")}
        </button>
      </div>
    </div>
  );
}

function BuilderTreeNodeView({
  actionsDisabled,
  dirtyNodeIds,
  draggedComponentId,
  expandedNodeIds,
  getMenuItems,
  node,
  onDragComponentChange,
  onMoveComponent,
  onRunMenuAction,
  onSelect,
  onToggleExpanded,
  selectedId,
  selectedIds,
  t,
}: {
  actionsDisabled: boolean;
  dirtyNodeIds: ReadonlySet<string>;
  draggedComponentId: string | null;
  expandedNodeIds: ReadonlySet<string>;
  getMenuItems: (node: BuilderTreeNode) => readonly BuilderTreeMenuItem[];
  node: BuilderTreeNode;
  onDragComponentChange: (componentId: string | null) => void;
  onMoveComponent: (componentId: string, parentUuid: string | null) => void;
  onRunMenuAction: (runAction: BuilderTreeMenuAction) => void;
  onSelect: (
    selection: InterfaceBuilderSelection,
    modifier: TreeSelectModifier,
  ) => void;
  onToggleExpanded: (nodeId: string) => void;
  selectedId: string | null;
  selectedIds: ReadonlySet<string>;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const selected = node.id === selectedId;
  // Ctrl/Cmd-picked siblings stay highlighted next to the primary node, which
  // keeps the stronger `--selected` treatment.
  const coSelected = !selected && selectedIds.has(node.id);
  const componentId = node.selection.kind === "component"
    ? getTreeNodeActionComponentId(node)
    : null;
  const isDropTarget = node.selection.kind === "windows" || componentId !== null;
  const handleDragStart = (event: DragEvent<HTMLButtonElement>) => {
    if (!componentId || actionsDisabled) return;

    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("application/x-interface-builder-component", componentId);
    onDragComponentChange(componentId);
  };
  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!isDropTarget || actionsDisabled) return;
    if (!draggedComponentId || draggedComponentId === componentId) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };
  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (!isDropTarget || actionsDisabled) return;

    const draggedId = event.dataTransfer.getData(
      "application/x-interface-builder-component",
    ) || draggedComponentId;
    if (!draggedId || draggedId === componentId) return;

    event.preventDefault();
    onMoveComponent(draggedId, node.selection.kind === "windows" ? null : componentId);
    onDragComponentChange(null);
  };
  const dirty = dirtyNodeIds.has(node.id);
  const hasChildren = node.children.length > 0;
  const expanded = hasChildren && expandedNodeIds.has(node.id);
  const menuItems = getMenuItems(node);
  const hasMenu = menuItems.length > 0;
  const handleTreeButtonKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (event.key !== "Delete") return;
    if (!selected) return;

    // The key runs the entry the menu of the node carries, so a component and
    // a menu item are each deleted the way their own menu deletes them - a
    // multi-selection through the entry that deletes all of it.
    const deleteMenuItemIds = node.selection.kind === "component"
      ? ["delete-components", "delete-component"]
      : node.selection.kind === "sidebarItem"
        ? ["delete-sidebar-item"]
        : [];
    if (deleteMenuItemIds.length === 0) return;

    const deleteMenuItem = menuItems.find(
      (item) => deleteMenuItemIds.includes(item.id),
    );
    if (
      actionsDisabled
      || deleteMenuItem?.disabled === true
      || !deleteMenuItem?.run
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    onRunMenuAction(deleteMenuItem.run);
  };
  const tooltip = node.tooltip;
  const tooltipContent = tooltip ? (
    <>
      <div className="interface-builder-inspector__tree-tooltip-line">
        <span className="interface-builder-inspector__tree-tooltip-label">
          {t("interfaceBuilderInspector.treeTooltip.id")}:
        </span>
        <span className="interface-builder-inspector__tree-tooltip-value">
          {tooltip.id}
        </span>
      </div>
      {tooltip.dates ? (
        <>
          <div className="interface-builder-inspector__tree-tooltip-line">
            <span className="interface-builder-inspector__tree-tooltip-label">
              {t("interfaceBuilderInspector.treeTooltip.createdAt")}:
            </span>
            <span className="interface-builder-inspector__tree-tooltip-value">
              {formatTreeNodeDate(tooltip.dates.createdAt, t)}
            </span>
          </div>
          <div className="interface-builder-inspector__tree-tooltip-line">
            <span className="interface-builder-inspector__tree-tooltip-label">
              {t("interfaceBuilderInspector.treeTooltip.updatedAt")}:
            </span>
            <span className="interface-builder-inspector__tree-tooltip-value">
              {formatTreeNodeDate(tooltip.dates.updatedAt, t)}
            </span>
          </div>
        </>
      ) : null}
    </>
  ) : null;
  const treeButton = (
    <button
      className={[
        "interface-builder-inspector__tree-button",
        selected ? "interface-builder-inspector__tree-button--selected" : "",
        coSelected ? "interface-builder-inspector__tree-button--co-selected" : "",
        dirty ? "interface-builder-inspector__tree-button--dirty" : "",
      ].filter(Boolean).join(" ")}
      type="button"
      aria-label={node.label}
      aria-pressed={selected || coSelected}
      data-interface-builder-tree-selected={selected ? "true" : undefined}
      draggable={componentId !== null && !actionsDisabled}
      onDragStart={handleDragStart}
      onDragEnd={() => onDragComponentChange(null)}
      onClick={(event) => onSelect(
        node.selection,
        readTreeSelectModifier(event),
      )}
      onKeyDown={handleTreeButtonKeyDown}
    >
      {node.icon ? (
        <span
          aria-hidden="true"
          className={[
            "interface-builder-inspector__tree-icon",
            `interface-builder-inspector__tree-icon--${node.icon}`,
          ].join(" ")}
        />
      ) : null}
      <span className="interface-builder-inspector__tree-label">
        {node.displayLabel ?? node.label}
      </span>
    </button>
  );

  const treeRow = (
    <div
      className={[
        "interface-builder-inspector__tree-row",
        draggedComponentId === componentId
          ? "interface-builder-inspector__tree-row--dragging"
          : "",
      ].filter(Boolean).join(" ")}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {hasChildren ? (
        <button
          className="interface-builder-inspector__tree-expander"
          type="button"
          aria-label={t(
            expanded
              ? "interfaceBuilderInspector.collapseNode"
              : "interfaceBuilderInspector.expandNode",
            { name: node.label },
          )}
          aria-expanded={expanded}
          onClick={() => onToggleExpanded(node.id)}
        >
          <span aria-hidden="true">{expanded ? "-" : "+"}</span>
        </button>
      ) : (
        <span className="interface-builder-inspector__tree-spacer" />
      )}
      {tooltipContent ? (
        <Tooltip
          className="interface-builder-inspector__tree-tooltip"
          content={tooltipContent}
          delay={TREE_NODE_TOOLTIP_DELAY_MS}
          followCursor
          placement="bottom-start"
        >
          {treeButton}
        </Tooltip>
      ) : treeButton}
    </div>
  );

  return (
    <li className="interface-builder-inspector__tree-item">
      {hasMenu ? (
        <RadixContextMenu.Root>
          <RadixContextMenu.Trigger asChild>
            {treeRow}
          </RadixContextMenu.Trigger>
          <RadixContextMenu.Portal>
            <RadixContextMenu.Content
              aria-label={t("interfaceBuilderInspector.treeMenu.label", {
                name: node.label,
              })}
              className="interface-builder-inspector__tree-menu"
              collisionPadding={12}
              loop
              style={TREE_CONTEXT_MENU_STYLE}
            >
              <BuilderTreeMenuEntries
                actionsDisabled={actionsDisabled}
                items={menuItems}
                onRunMenuAction={onRunMenuAction}
                t={t}
              />
            </RadixContextMenu.Content>
          </RadixContextMenu.Portal>
        </RadixContextMenu.Root>
      ) : treeRow}
      {expanded ? (
        <ul className="interface-builder-inspector__tree-children">
          {node.children.map((child) => (
            <BuilderTreeNodeView
              actionsDisabled={actionsDisabled}
              dirtyNodeIds={dirtyNodeIds}
              draggedComponentId={draggedComponentId}
              expandedNodeIds={expandedNodeIds}
              getMenuItems={getMenuItems}
              key={child.id}
              node={child}
              onDragComponentChange={onDragComponentChange}
              onMoveComponent={onMoveComponent}
              onRunMenuAction={onRunMenuAction}
              onSelect={onSelect}
              onToggleExpanded={onToggleExpanded}
              selectedId={selectedId}
              selectedIds={selectedIds}
              t={t}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function BuilderTreeMenuEntries({
  actionsDisabled,
  emptyLabel,
  items,
  onRunMenuAction,
  searchLabel,
  t,
}: {
  actionsDisabled: boolean;
  emptyLabel?: string;
  items: readonly BuilderTreeMenuItem[];
  onRunMenuAction: (runAction: BuilderTreeMenuAction) => void;
  searchLabel?: string;
  t: (key: string) => string;
}) {
  const [query, setQuery] = useState("");
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visibleItems = normalizedQuery
    ? items.filter((item) => item.label.toLocaleLowerCase().includes(normalizedQuery))
    : items;

  return (
    <>
      {searchLabel ? (
        <input
          aria-label={searchLabel}
          autoFocus
          className="interface-builder-inspector__tree-menu-search"
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => event.stopPropagation()}
          placeholder={searchLabel}
          type="search"
          value={query}
        />
      ) : null}
      {visibleItems.length === 0 && emptyLabel ? (
        <p className="interface-builder-inspector__tree-menu-empty">{emptyLabel}</p>
      ) : null}
      {visibleItems.map((item) => {
        const itemClassName = [
          "interface-builder-inspector__tree-menu-item",
          item.variant === "danger"
            ? "interface-builder-inspector__tree-menu-item--danger"
            : "",
        ].filter(Boolean).join(" ");
        const disabled = (
          (actionsDisabled && item.disableWhenActionsDisabled !== false)
          || item.disabled === true
        );
        const separator = item.separatorBefore ? (
          <RadixContextMenu.Separator
            className="interface-builder-inspector__tree-menu-separator"
          />
        ) : null;

        if (item.children?.length) {
          return (
            <Fragment key={item.id}>
              {separator}
              <RadixContextMenu.Sub>
                <RadixContextMenu.SubTrigger
                  className={itemClassName}
                  disabled={disabled}
                >
                  <span className="interface-builder-inspector__tree-menu-label">
                    {item.label}
                  </span>
                  <span
                    className="interface-builder-inspector__tree-menu-chevron"
                    aria-hidden="true"
                  >
                    &rsaquo;
                  </span>
                </RadixContextMenu.SubTrigger>
                <RadixContextMenu.Portal>
                  <RadixContextMenu.SubContent
                    aria-label={item.label}
                    className={[
                      "interface-builder-inspector__tree-menu",
                      "interface-builder-inspector__tree-menu--submenu",
                    ].join(" ")}
                    collisionPadding={12}
                    sideOffset={8}
                    style={TREE_CONTEXT_MENU_STYLE}
                  >
                    <BuilderTreeMenuEntries
                      actionsDisabled={actionsDisabled}
                      emptyLabel={item.searchable
                        ? t("interfaceBuilderInspector.treeMenu.searchEmpty")
                        : undefined}
                      items={item.children}
                      onRunMenuAction={onRunMenuAction}
                      t={t}
                      searchLabel={item.searchable
                        ? t("interfaceBuilderInspector.treeMenu.searchComponents")
                        : undefined}
                    />
                  </RadixContextMenu.SubContent>
                </RadixContextMenu.Portal>
              </RadixContextMenu.Sub>
            </Fragment>
          );
        }

        return (
          <Fragment key={item.id}>
            {separator}
            <RadixContextMenu.Item
              className={itemClassName}
              disabled={disabled || !item.run}
              onSelect={() => {
                if (item.run) onRunMenuAction(item.run);
              }}
            >
              <span className="interface-builder-inspector__tree-menu-label">
                {item.label}
              </span>
            </RadixContextMenu.Item>
          </Fragment>
        );
      })}
    </>
  );
}

function createCopyIdTreeMenuItem(
  id: string,
  t: (key: string, options?: Record<string, unknown>) => string,
  copyToClipboard: (value: string) => Promise<void>,
): BuilderTreeMenuItem {
  return {
    id: `copy-id:${id}`,
    label: t("interfaceBuilderInspector.treeMenu.copyId"),
    run: () => copyToClipboard(id),
  };
}

function createTreeExpansionMenuItems(
  node: BuilderTreeNode,
  t: (key: string, options?: Record<string, unknown>) => string,
  setSubtreeExpanded: (node: BuilderTreeNode, expanded: boolean) => void,
): readonly BuilderTreeMenuItem[] {
  if (node.children.length === 0) return [];

  return [
    {
      disableWhenActionsDisabled: false,
      id: "expand-subtree",
      label: t("interfaceBuilderInspector.treeMenu.expandSubtree"),
      run: () => setSubtreeExpanded(node, true),
    },
    {
      disableWhenActionsDisabled: false,
      id: "collapse-subtree",
      label: t("interfaceBuilderInspector.treeMenu.collapseSubtree"),
      run: () => setSubtreeExpanded(node, false),
    },
  ];
}

function withFirstSeparator(
  items: readonly BuilderTreeMenuItem[],
): readonly BuilderTreeMenuItem[] {
  return items.map((item, index) => (
    index === 0 ? { ...item, separatorBefore: true } : item
  ));
}

function collectExpandableTreeNodeIds(
  node: BuilderTreeNode,
): readonly string[] {
  if (node.children.length === 0) return [];

  return [
    node.id,
    ...node.children.flatMap((child) => collectExpandableTreeNodeIds(child)),
  ];
}

/**
 * The project the record a tree node stands for belongs to, when it stands for
 * one at all: the system nodes the tree is hung from belong to no project.
 */
function readTreeNodeProjectId(
  node: BuilderTreeNode | null,
  components: readonly StoredUiComponent[],
  sidebarItems: readonly StoredUiSidebarItem[],
): string | null {
  if (!node) return null;

  const componentId = getTreeNodeActionComponentId(node);
  if (componentId) {
    return components.find(
      (component) => component.uuid === componentId,
    )?.projectId ?? null;
  }

  const { selection } = node;
  if (selection.kind !== "sidebarItem") return null;

  return sidebarItems.find(
    (item) => item.uuid === selection.sidebarItemId,
  )?.projectId ?? null;
}

function getTreeNodeActionComponentId(node: BuilderTreeNode): string | null {
  if (node.selection.kind !== "component") return null;

  return node.componentUuid
    ?? (node.selection.componentPath === null ? node.selection.componentId : null);
}

function createBuilderTree({
  components,
  sidebarItems,
  t,
}: {
  components: readonly StoredUiComponent[];
  sidebarItems: readonly StoredUiSidebarItem[];
  t: (key: string, options?: Record<string, unknown>) => string;
}): BuilderTreeNode {
  const sidebarNode = createSidebarNode(sidebarItems, t);
  const windowsNode = createWindowsNode(components, t);

  return {
    id: WORKSPACE_NODE_ID,
    label: t("interfaceBuilderInspector.nodes.workspace"),
    selection: {
      kind: "workspace",
      id: WORKSPACE_NODE_ID,
    },
    properties: [],
    children: [
      sidebarNode,
      windowsNode,
    ],
  };
}

function createWindowsNode(
  components: readonly StoredUiComponent[],
  t: (key: string, options?: Record<string, unknown>) => string,
): BuilderTreeNode {
  const componentChildrenByParentUuid = createComponentChildrenIndex(components);
  const componentNodes = sortTreeComponents(
    components.filter((component) => component.parentUuid === null),
  ).map((component) => createComponentRootNode({
    component,
    componentChildrenByParentUuid,
    t,
  }));

  return {
    id: WINDOWS_NODE_ID,
    label: t("interfaceBuilderInspector.nodes.windows"),
    selection: {
      kind: "windows",
      id: WINDOWS_NODE_ID,
    },
    properties: [],
    children: componentNodes,
  };
}

function createSidebarNode(
  sidebarItems: readonly StoredUiSidebarItem[],
  t: (key: string, options?: Record<string, unknown>) => string,
): BuilderTreeNode {
  return {
    id: SIDEBAR_NODE_ID,
    label: t("interfaceBuilderInspector.nodes.sidebar"),
    selection: {
      kind: "sidebar",
      id: SIDEBAR_NODE_ID,
    },
    properties: [],
    children: sidebarItems.map((item) => createSidebarItemNode(item, t)),
  };
}

function createSidebarItemNode(
  item: StoredUiSidebarItem,
  t: (key: string, options?: Record<string, unknown>) => string,
): BuilderTreeNode {
  const componentId = getUiSidebarItemComponentId(item);

  return {
    id: `sidebar-item:${item.uuid}`,
    label: `${t("interfaceBuilderInspector.nodeKinds.sidebarItem")}: ${item.name || t("interfaceBuilderInspector.nodes.sidebarItemFallback")}`,
    displayLabel: item.name || t("interfaceBuilderInspector.nodes.sidebarItemFallback"),
    icon: "sidebarItem",
    tooltip: {
      id: item.uuid,
      dates: {
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      },
    },
    selection: {
      kind: "sidebarItem",
      id: `sidebar-item:${item.uuid}`,
      componentId,
      sidebarItemId: item.uuid,
    },
    properties: createSidebarItemBuilderProperties(item),
    children: [],
  };
}

type ComponentChildrenIndex = ReadonlyMap<string, readonly StoredUiComponent[]>;

function createComponentRootNode({
  component,
  componentChildrenByParentUuid,
  t,
}: {
  component: StoredUiComponent;
  componentChildrenByParentUuid: ComponentChildrenIndex;
  t: (key: string, options?: Record<string, unknown>) => string;
}): BuilderTreeNode {
  const componentChildren = createComponentNodes({
    componentChildrenByParentUuid,
    parentComponent: component,
    rootComponent: component,
    rawChildren: readChildren(component.uiScheme),
    t,
  });

  return {
    componentUuid: component.uuid,
    id: `component:${component.uuid}`,
    label: `${t("interfaceBuilderInspector.nodeKinds.component")}: ${component.name}`,
    displayLabel: component.name,
    icon: "component",
    tooltip: {
      id: component.uuid,
    },
    selection: {
      kind: "component",
      componentId: component.uuid,
      componentKind: component.componentType,
      componentPath: null,
      id: `component:${component.uuid}`,
    },
    properties: createComponentBuilderProperties(component),
    children: componentChildren,
  };
}

function createComponentNodes({
  componentChildrenByParentUuid,
  parentComponent,
  rootComponent,
  parentPath = [],
  rawChildren,
  t,
}: {
  componentChildrenByParentUuid: ComponentChildrenIndex;
  parentComponent: StoredUiComponent | null;
  rootComponent: StoredUiComponent;
  parentPath?: readonly number[];
  rawChildren: readonly unknown[];
  t: (key: string, options?: Record<string, unknown>) => string;
}): BuilderTreeNode[] {
  const childComponents = parentComponent
    ? sortTreeComponents(
      componentChildrenByParentUuid.get(parentComponent.uuid) ?? [],
    )
    : [];
  const consumedComponentUuids = new Set<string>();
  const rawChildNodes = rawChildren.map((rawComponent, index) => {
    const component = resolveRawChildComponent(
      rawComponent,
      childComponents,
      consumedComponentUuids,
    );
    if (component) consumedComponentUuids.add(component.uuid);

    return createComponentChildNode({
      component,
      componentChildrenByParentUuid,
      index,
      parentPath,
      rawComponent,
      rootComponent,
      t,
    });
  });
  const remainingComponentNodes = childComponents
    .filter((component) => !consumedComponentUuids.has(component.uuid))
    .map((component, index) => createComponentChildNode({
      component,
      componentChildrenByParentUuid,
      index: rawChildren.length + index,
      parentPath,
      rawComponent: component.uiScheme,
      rootComponent,
      t,
    }));

  return [
    ...rawChildNodes,
    ...remainingComponentNodes,
  ];
}

function createComponentChildNode({
  component,
  componentChildrenByParentUuid,
  index,
  parentPath,
  rawComponent,
  rootComponent,
  t,
}: {
  component: StoredUiComponent | null;
  componentChildrenByParentUuid: ComponentChildrenIndex;
  index: number;
  parentPath: readonly number[];
  rawComponent: unknown;
  rootComponent: StoredUiComponent;
  t: (key: string, options?: Record<string, unknown>) => string;
}): BuilderTreeNode {
  const componentPath = [...parentPath, index];
  const componentPathLabel = componentPath.join(".");
  const rawRecord = isRecord(rawComponent) ? rawComponent : {};
  const componentProps = readRecordProps(rawRecord);
  const componentKind = component?.componentType
    ?? readString(componentProps.kind)
    ?? readString(rawRecord.kind)
    ?? readString(rawRecord.type)
    ?? t("interfaceBuilderInspector.nodes.unknownComponentKind");
  const label = component?.name
    ?? readString(componentProps.name)
    ?? readString(rawRecord.name)
    ?? readString(componentProps.title)
    ?? readString(rawRecord.title)
    ?? t("interfaceBuilderInspector.nodes.componentFallback", {
      index: index + 1,
    });
  const children = createComponentNodes({
    componentChildrenByParentUuid,
    parentComponent: component,
    rootComponent,
    parentPath: componentPath,
    rawChildren: readChildren(component?.uiScheme ?? rawComponent),
    t,
  });
  const id = `component:${rootComponent.uuid}:${componentPathLabel}`;

  return {
    componentUuid: component?.uuid,
    id,
    label: `${componentKind}: ${label}`,
    displayLabel: label,
    icon: "component",
    tooltip: {
      id: component?.uuid ?? id,
    },
    selection: {
      kind: "component",
      id,
      componentKind,
      componentPath: componentPathLabel,
      componentId: rootComponent.uuid,
    },
    properties: component
      ? createComponentBuilderProperties(component)
      : createRecordBuilderPropertiesFromRawObject(rawRecord),
    children,
  };
}

function createComponentChildrenIndex(
  components: readonly StoredUiComponent[],
): ComponentChildrenIndex {
  const childrenByParentUuid = new Map<string, StoredUiComponent[]>();

  components.forEach((component) => {
    if (!component.parentUuid) return;

    const children = childrenByParentUuid.get(component.parentUuid) ?? [];
    children.push(component);
    childrenByParentUuid.set(component.parentUuid, children);
  });

  childrenByParentUuid.forEach((children, parentUuid) => {
    childrenByParentUuid.set(parentUuid, sortTreeComponents(children));
  });

  return childrenByParentUuid;
}

function resolveRawChildComponent(
  rawComponent: unknown,
  childComponents: readonly StoredUiComponent[],
  consumedComponentUuids: ReadonlySet<string>,
): StoredUiComponent | null {
  const componentUuid = readRawComponentUuid(rawComponent);
  if (componentUuid) {
    return childComponents.find((component) => (
      component.uuid === componentUuid
      && !consumedComponentUuids.has(component.uuid)
    )) ?? null;
  }

  return childComponents.find((component) => (
    !consumedComponentUuids.has(component.uuid)
    && rawComponentMatchesComponent(rawComponent, component)
  )) ?? null;
}

function rawComponentMatchesComponent(
  rawComponent: unknown,
  component: StoredUiComponent,
): boolean {
  if (!isRecord(rawComponent)) return false;

  const props = readRecordProps(rawComponent);
  const name = readString(props.name)
    ?? readString(rawComponent.name)
    ?? readString(props.title)
    ?? readString(rawComponent.title);
  const kind = readString(props.kind)
    ?? readString(rawComponent.kind)
    ?? readString(rawComponent.type);

  return name === component.name && kind === component.componentType;
}

function readRawComponentUuid(value: unknown): string | null {
  if (!isRecord(value)) return null;

  const props = readRecordProps(value);

  return normalizeUiComponentId(
    readString(value.uuid)
    ?? readString(value.component_uuid)
    ?? readString(props.uuid)
    ?? readString(props.component_uuid),
  );
}

function sortTreeComponents<T extends StoredUiComponent>(
  components: readonly T[],
): T[] {
  return [...components].sort((a, b) => (
    readNumber(a.uiScheme.position) - readNumber(b.uiScheme.position)
    || a.name.localeCompare(b.name)
    || a.uuid.localeCompare(b.uuid)
  ));
}

function selectionReferencesComponent(
  selection: InterfaceBuilderSelection | null,
  componentId: string,
): boolean {
  if (!selection) return false;

  return (
    (
      selection.kind === "component"
      || selection.kind === "sidebarItem"
    )
    && selection.componentId === componentId
  );
}

function readAccountComponentRouteId(pathname: string): string | null {
  const routePrefix = `${ROUTES.account}/components/`;
  if (!pathname.startsWith(routePrefix)) return null;

  const rawComponentId = pathname.slice(routePrefix.length).split("/")[0];
  if (!rawComponentId) return null;

  try {
    return normalizeUiComponentId(decodeURIComponent(rawComponentId));
  } catch {
    return normalizeUiComponentId(rawComponentId);
  }
}

function createInitialWindowState(): InterfaceBuilderInspectorWindowState {
  const storedState = readInterfaceBuilderInspectorWindowState();
  const defaultState = createDefaultWindowState();

  if (!storedState) return defaultState;

  return normalizeWindowState({
    ...defaultState,
    ...storedState,
    expandedNodeIds: storedState.expandedNodeIds,
  });
}

function createDefaultWindowState(): InterfaceBuilderInspectorWindowState {
  const viewportWidth = getViewportWidth();
  return {
    expandedNodeIds: DEFAULT_EXPANDED_NODE_IDS,
    height: DEFAULT_WINDOW_HEIGHT,
    left: Math.max(
      VIEWPORT_MARGIN,
      viewportWidth - DEFAULT_WINDOW_WIDTH - VIEWPORT_MARGIN,
    ),
    top: 84,
    treeHeight: DEFAULT_TREE_HEIGHT,
    width: DEFAULT_WINDOW_WIDTH,
  };
}

function normalizeWindowState(
  state: InterfaceBuilderInspectorWindowState,
): InterfaceBuilderInspectorWindowState {
  const viewportWidth = getViewportWidth();
  const viewportHeight = getViewportHeight();
  const width = clamp(
    Math.round(state.width),
    MIN_WINDOW_WIDTH,
    Math.max(MIN_WINDOW_WIDTH, viewportWidth - VIEWPORT_MARGIN * 2),
  );
  const height = clamp(
    Math.round(state.height),
    MIN_WINDOW_HEIGHT,
    Math.max(MIN_WINDOW_HEIGHT, viewportHeight - VIEWPORT_MARGIN * 2),
  );

  return {
    expandedNodeIds: state.expandedNodeIds,
    height,
    left: clamp(
      Math.round(state.left),
      VIEWPORT_MARGIN,
      Math.max(VIEWPORT_MARGIN, viewportWidth - width - VIEWPORT_MARGIN),
    ),
    top: clamp(
      Math.round(state.top),
      VIEWPORT_MARGIN,
      Math.max(VIEWPORT_MARGIN, viewportHeight - height - VIEWPORT_MARGIN),
    ),
    treeHeight: clampTreeHeight(state.treeHeight, height),
    width,
  };
}

function resizeInspectorWindow(
  startState: InterfaceBuilderInspectorWindowState,
  edge: ResizeEdge,
  deltaX: number,
  deltaY: number,
): InterfaceBuilderInspectorWindowState {
  let left = startState.left;
  let top = startState.top;
  let width = startState.width;
  let height = startState.height;

  if (edge.includes("e")) {
    width += deltaX;
  }

  if (edge.includes("s")) {
    height += deltaY;
  }

  if (edge.includes("w")) {
    left += deltaX;
    width -= deltaX;
  }

  if (edge.includes("n")) {
    top += deltaY;
    height -= deltaY;
  }

  return normalizeWindowState({
    ...startState,
    height,
    left,
    top,
    width,
  });
}

function clampTreeHeight(value: number, windowHeight: number): number {
  return clamp(
    Math.round(value),
    MIN_TREE_HEIGHT,
    getMaxTreeHeight(windowHeight),
  );
}

function getMaxTreeHeight(windowHeight: number): number {
  return Math.max(
    MIN_TREE_HEIGHT,
    windowHeight
      - INSPECTOR_CHROME_HEIGHT
      - SPLITTER_HEIGHT
      - MIN_PROPERTIES_HEIGHT,
  );
}

function findTreeNodePath(
  node: BuilderTreeNode,
  id: string,
): BuilderTreeNode[] | null {
  if (node.id === id) return [node];

  for (const child of node.children) {
    const match = findTreeNodePath(child, id);
    if (match) return [node, ...match];
  }

  return null;
}

/**
 * What the modifiers of a tree click ask for. Shift wins over Ctrl/Cmd when
 * both are held: a range is the wider of the two selections.
 */
function readTreeSelectModifier(event: {
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
}): TreeSelectModifier {
  if (event.shiftKey) return "range";
  if (event.ctrlKey || event.metaKey) return "additive";

  return null;
}

/**
 * The nodes of the tree in the order they are displayed, collapsed subtrees
 * left out: those are shown as the single row of their parent, so a range over
 * them selects that row alone rather than descendants the click never passed.
 */
function collectVisibleTreeNodes(
  node: BuilderTreeNode,
  expandedNodeIds: ReadonlySet<string>,
): BuilderTreeNode[] {
  const expanded = node.children.length > 0 && expandedNodeIds.has(node.id);

  return [
    node,
    ...(expanded
      ? node.children.flatMap(
        (child) => collectVisibleTreeNodes(child, expandedNodeIds),
      )
      : []),
  ];
}

/**
 * The selections of every displayed node between `anchorId` and `targetId`,
 * inclusive, ending with the target so it becomes the primary selection the
 * properties panel follows.
 *
 * Returns null when either end is not a displayed node -- there is no range to
 * speak of, and the caller falls back to a plain single selection.
 */
function collectTreeNodeRange(
  tree: BuilderTreeNode,
  expandedNodeIds: ReadonlySet<string>,
  anchorId: string | null,
  targetId: string,
): readonly InterfaceBuilderSelection[] | null {
  if (!anchorId) return null;
  if (anchorId === targetId) return null;

  const visibleNodes = collectVisibleTreeNodes(tree, expandedNodeIds);
  const anchorIndex = visibleNodes.findIndex((node) => node.id === anchorId);
  const targetIndex = visibleNodes.findIndex((node) => node.id === targetId);
  if (anchorIndex < 0 || targetIndex < 0) return null;

  const range = visibleNodes
    .slice(
      Math.min(anchorIndex, targetIndex),
      Math.max(anchorIndex, targetIndex) + 1,
    )
    .map((node) => node.selection);

  // Selected upwards, so the range is reversed to keep the clicked node last.
  return anchorIndex > targetIndex ? [...range].reverse() : range;
}

function readChildren(value: unknown): unknown[] {
  if (!isRecord(value)) return [];
  if (Array.isArray(value.children)) return value.children;

  const props = readRecordProps(value);
  if (Array.isArray(props.children)) return props.children;

  return [];
}

function readRecordProps(value: Record<string, unknown>): Record<string, unknown> {
  return isRecord(value.props) ? value.props : {};
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function readNumber(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;

  const parsed = typeof value === "string" ? Number(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatTreeNodeDate(
  value: string | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (!value) return t("interfaceBuilderInspector.treeTooltip.missingDate");

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return t("interfaceBuilderInspector.treeTooltip.invalidDate");
  }

  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
    second: "2-digit",
    year: "numeric",
  }).formatToParts(date).map((part) => (
    part.type === "second" ? `${part.value}.${readMicroseconds(value)}` : part.value
  )).join("");
}

function readMicroseconds(value: string): string {
  const [, fraction = ""] = value.match(/\.(\d+)/) ?? [];
  if (fraction) return fraction.padEnd(6, "0").slice(0, 6);

  const millisecondMatch = new Date(value).getUTCMilliseconds().toString().padStart(3, "0");
  return `${millisecondMatch}000`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function getViewportWidth(): number {
  return typeof window === "undefined" ? 1200 : window.innerWidth;
}

function getViewportHeight(): number {
  return typeof window === "undefined" ? 800 : window.innerHeight;
}

function getCenteredObjectEditorPosition(
  measuredWidth?: number,
  measuredHeight?: number,
): { left: number; top: number } {
  const viewportWidth = getViewportWidth();
  const viewportHeight = getViewportHeight();
  const width = Math.min(
    OBJECT_EDITOR_MAX_WIDTH,
    measuredWidth ?? Math.max(0, viewportWidth - VIEWPORT_MARGIN * 2),
  );
  const height = Math.min(
    OBJECT_EDITOR_ESTIMATED_HEIGHT,
    measuredHeight ?? Math.max(0, viewportHeight - VIEWPORT_MARGIN * 2),
  );
  const maxLeft = Math.max(
    VIEWPORT_MARGIN,
    viewportWidth - width - VIEWPORT_MARGIN,
  );
  const maxTop = Math.max(
    VIEWPORT_MARGIN,
    viewportHeight - height - VIEWPORT_MARGIN,
  );

  return {
    left: clamp(
      Math.round((viewportWidth - width) / 2),
      VIEWPORT_MARGIN,
      maxLeft,
    ),
    top: clamp(
      Math.round((viewportHeight - height) / 2),
      VIEWPORT_MARGIN,
      maxTop,
    ),
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
